import { useState, useRef, useEffect } from "react";
import { Link } from "react-router-dom";
import mkcgLogo from "../assests/logo/logo.png";
const API_URL = process.env.REACT_APP_API_URL || "http://localhost:5001/api";

/* ── Gender code → label (st_gender is stored as 1=Male, 2=Female) ────── */
function genderLabel(g) {
  const v = String(g);
  if (v === "1") return "Male";
  if (v === "2") return "Female";
  return g || "—";
}
/* ── Date formatter (any parseable date → DD-MM-YYYY) ──────────────────── */
function formatDate(d) {
  if (!d) return "—";
  const date = new Date(d);
  if (isNaN(date.getTime())) return String(d);
  const dd = String(date.getDate()).padStart(2, "0");
  const mm = String(date.getMonth() + 1).padStart(2, "0");
  const yyyy = date.getFullYear();
  return `${dd}-${mm}-${yyyy}`;
}
/* ── Captcha generator ─────────────────────────────────────────────────── */
function generateCaptcha() {
  const chars = "ABCDEFGHJKLMNPQRSTUVWXYZabcdefghjkmnpqrstuvwxyz23456789";
  return Array.from({ length: 6 }, () => chars[Math.floor(Math.random() * chars.length)]).join("");
}

/* ── File validator: checks mime type + size range (in KB) ─────────────── */
function validateFile(file, { types, minKB = 0, maxKB, label }) {
  if (!file) return `${label} is required`;

  const ext = file.name.split(".").pop().toLowerCase();
  const typeOk =
    types.mimes.includes(file.type) || types.exts.includes(ext);

  if (!typeOk) {
    return `Invalid file type. Allowed: ${types.exts.join(", ").toUpperCase()}`;
  }

  const sizeKB = file.size / 1024;

  if (maxKB && sizeKB > maxKB) {
    return minKB
      ? `File size must be between ${minKB}-${maxKB}KB (yours is ${Math.round(sizeKB)}KB)`
      : `File size must not exceed ${maxKB}KB (yours is ${Math.round(sizeKB)}KB)`;
  }
  if (minKB && sizeKB < minKB) {
    return `File size must be between ${minKB}-${maxKB}KB (yours is ${Math.round(sizeKB)}KB)`;
  }

  return null; // valid
}

/* ── Captcha Canvas ────────────────────────────────────────────────────── */
const CaptchaCanvas = ({ text }) => {
  const ref = useRef(null);
  useEffect(() => {
    const canvas = ref.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    ctx.clearRect(0, 0, 140, 44);
    ctx.fillStyle = "rgba(15,45,107,0.6)";
    ctx.fillRect(0, 0, 140, 44);
    for (let i = 0; i < 5; i++) {
      ctx.strokeStyle = `rgba(${Math.random()*200},${Math.random()*200},255,0.3)`;
      ctx.beginPath();
      ctx.moveTo(Math.random() * 140, Math.random() * 44);
      ctx.lineTo(Math.random() * 140, Math.random() * 44);
      ctx.stroke();
    }
    for (let i = 0; i < 30; i++) {
      ctx.fillStyle = `rgba(255,255,255,${Math.random() * 0.2})`;
      ctx.fillRect(Math.random() * 140, Math.random() * 44, 2, 2);
    }
    const fonts = ["Georgia", "serif", "cursive"];
    const colors = ["#2b68c3", "#2b68c3", "#a0c8ff", "#fff"];
    [...text].forEach((ch, i) => {
      ctx.save();
      ctx.font = `bold ${22 + Math.random() * 6}px ${fonts[i % fonts.length]}`;
      ctx.fillStyle = colors[i % colors.length];
      ctx.translate(10 + i * 20, 28 + Math.random() * 6 - 3);
      ctx.rotate((Math.random() - 0.5) * 0.35);
      ctx.fillText(ch, 0, 0);
      ctx.restore();
    });
  }, [text]);
  return <canvas ref={ref} width={140} height={44} style={{ borderRadius: 8 }} />;
};

/* ── Section Heading ───────────────────────────────────────────────────── */
const SectionHeading = ({ children }) => (
  <div className="stureg-section-heading">
    <span className="stureg-section-dot" />
    {children}
  </div>
);

/* ── Field wrapper ─────────────────────────────────────────────────────── */
const Field = ({ label, required, error, children }) => (
  <div className="stureg-field">
    <label className="stureg-label">{label}{required && <span className="stureg-req"> *</span>}</label>
    {children}
    {error && <p className="stureg-error-msg">⚠ {error}</p>}
  </div>
);

/* ── Input ─────────────────────────────────────────────────────────────── */
const Input = ({ error, ...props }) => (
  <div className={`stureg-input-wrap${error ? " stureg-err" : ""}`}>
    <input {...props} />
  </div>
);

/* ── Select ────────────────────────────────────────────────────────────── */
const Select = ({ error, children, ...props }) => (
  <div className={`stureg-input-wrap stureg-select-wrap${error ? " stureg-err" : ""}`}>
    <select {...props}>{children}</select>
    <svg className="stureg-select-arrow" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
      <polyline points="6 9 12 15 18 9" />
    </svg>
  </div>
);

/* ── FileInput ─────────────────────────────────────────────────────────── */
const FileInput = ({ hint, error, onChange, value, existingUrl, existingName }) => (
  <div className={`stureg-file-wrap${error ? " stureg-err" : ""}`}>
    <label className="stureg-file-label">
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4" />
        <polyline points="17 8 12 3 7 8" />
        <line x1="12" y1="3" x2="12" y2="15" />
      </svg>
      Choose file
      <input type="file" style={{ display: "none" }} onChange={onChange} accept=".pdf,.jpg,.jpeg,.png" />
    </label>
    <span className="stureg-file-name">
      {value ? (
        value.name
      ) : existingUrl ? (
        <>
          Uploaded:{" "}
          <a href={existingUrl} target="_blank" rel="noreferrer" style={{ color: "#2b68c3", fontWeight: 600 }}>
            {existingName || "view file"}
          </a>{" "}
          (choose a new file only to replace it)
        </>
      ) : (
        "No file chosen"
      )}
    </span>
    {hint && <span className="stureg-file-hint">{hint}</span>}
  </div>
);

/* ── Exam Row ──────────────────────────────────────────────────────────── */
const ExamRow = ({ row, idx, onChange, onRemove, onAdd, canRemove, rowErrors = {} }) => (
  <div className="stureg-exam-row">
    <div className={`stureg-input-wrap stureg-exam-input${rowErrors.examination ? " stureg-err" : ""}`}>
      <input placeholder="Examination" maxLength={50} value={row.examination} onChange={e => onChange(idx, "examination", e.target.value)} />
    </div>
    <div className={`stureg-input-wrap stureg-select-wrap stureg-exam-input${rowErrors.yearOfPassing ? " stureg-err" : ""}`}>
      <select value={row.yearOfPassing} onChange={e => onChange(idx, "yearOfPassing", e.target.value)}>
        <option value="">Year</option>
        {Array.from({ length: 30 }, (_, i) => new Date().getFullYear() - i).map((year) => (
          <option key={year} value={year}>{year}</option>
        ))}
      </select>
      <svg className="stureg-select-arrow" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="6 9 12 15 18 9" /></svg>
    </div>
    <div className={`stureg-input-wrap stureg-exam-input${rowErrors.college ? " stureg-err" : ""}`}>
      <input placeholder="Name of College" maxLength={50} value={row.college} onChange={e => onChange(idx, "college", e.target.value)} />
    </div>
    <div className={`stureg-input-wrap stureg-exam-input${rowErrors.board ? " stureg-err" : ""}`}>
      <input placeholder="Board/University" maxLength={50} value={row.board} onChange={e => onChange(idx, "board", e.target.value)} />
    </div>
    <div className="stureg-input-wrap stureg-exam-input">
      <input placeholder="Remarks" maxLength={100} value={row.remarks} onChange={e => onChange(idx, "remarks", e.target.value)} />
    </div>
    <button
      type="button"
      className={`stureg-exam-btn${canRemove ? " stureg-exam-remove" : ""}`}
      onClick={canRemove ? () => onRemove(idx) : onAdd}
      disabled={!canRemove && !onAdd}
      title={canRemove ? "Remove" : "Add row"}
    >
      {canRemove ? "✕" : "+"}
    </button>
  </div>
);

// ── Shared printable styles ──────────────────────────────────────────────
const printPreviewCSS_base = `
.pp-watermark{
  position:absolute;top:50%;left:50%;
  transform:translate(-50%,-50%) rotate(-35deg);
  font-family:'EB Garamond',serif;
  font-size:120pt;color:rgba(10,30,80,0.04);
  pointer-events:none;white-space:nowrap;z-index:0;
  font-weight:700;letter-spacing:0.1em;user-select:none;
}
.pp-header{
  display:flex;align-items:center;gap:14px;
  padding-bottom:7px;border-bottom:3px solid #184381;
  margin-bottom:5px;position:relative;z-index:1;
}
.pp-logo{width:62px;height:62px;object-fit:contain;flex-shrink:0;}
.pp-header-mid{flex:1;}
.pp-college-name{
  font-family:'EB Garamond',serif;font-size:17pt;font-weight:700;
  color:#184381;line-height:1.15;
}
.pp-college-odia{font-size:8.5pt;font-weight:600;color:#4a6fa5;margin-top:2px;}
.pp-college-sub{font-size:8pt;color:#184381;margin-top:3px;font-weight:600;letter-spacing:0.05em;text-transform:uppercase;}
.pp-header-right{text-align:right;flex-shrink:0;}
.pp-official-badge{
  display:inline-block;background:#184381;color:#fff;
  font-size:7pt;font-weight:700;letter-spacing:0.12em;
  text-transform:uppercase;padding:4px 11px;border-radius:3px;margin-bottom:5px;
}
.pp-print-date{font-size:7.5pt;color:#4a6fa5;}
.pp-title-bar{
  background:linear-gradient(135deg,#184381 0%,#2b68c3 100%);
  color:#fff;text-align:center;padding:10px 20px;
  border-radius:7px;margin-bottom:7px;position:relative;z-index:1;
}
.pp-title-main{font-family:'EB Garamond',serif;font-size:14pt;font-weight:700;letter-spacing:0.06em;text-transform:uppercase;}
.pp-title-sub{font-size:7.5pt;opacity:0.7;margin-top:2px;letter-spacing:0.04em;}
.pp-student-banner{
  display:flex;align-items:center;gap:16px;
  background:#eaf1fb;border:1.5px solid #c7d6ec;
  border-left:5px solid #184381;border-radius:8px;
  padding:10px 16px;margin-bottom:7px;position:relative;z-index:1;
}
.pp-photo-box{
  width:80px;height:100px;flex-shrink:0;
  border:2px solid #c7d6ec;border-radius:5px;
  background:#c7d6ec;display:flex;align-items:center;
  justify-content:center;overflow:hidden;
}
.pp-photo-placeholder{font-size:7.5pt;color:#6e93c9;text-align:center;padding:6px;}
.pp-student-grid{flex:1;display:grid;grid-template-columns:repeat(3,1fr);gap:8px 16px;}
.pp-si-item{display:flex;flex-direction:column;gap:2px;}
.pp-si-label{font-size:6.5pt;font-weight:700;color:#4a6fa5;text-transform:uppercase;letter-spacing:0.07em;}
.pp-si-val{font-size:9.5pt;font-weight:600;color:#184381;}
.pp-sig-box{width:90px;flex-shrink:0;display:flex;flex-direction:column;align-items:center;gap:5px;}
.pp-sig-img-box{
  width:90px;height:42px;border:1.5px solid #c7d6ec;
  border-radius:4px;background:#fff;display:flex;align-items:center;justify-content:center;
}
.pp-sig-placeholder{font-size:7pt;color:#6e93c9;}
.pp-sig-label{font-size:7pt;color:#4a6fa5;text-align:center;}
.pp-section{margin-bottom:5px;position:relative;z-index:1;page-break-inside:avoid;break-inside:avoid;}
.pp-sec-head{
  background:#184381;color:#fff;
  padding:6px 13px;border-radius:5px 5px 0 0;
  font-size:8pt;font-weight:700;letter-spacing:0.09em;text-transform:uppercase;
}
.pp-sec-body{
  border:1px solid #c7d6ec;border-top:none;
  border-radius:0 0 5px 5px;padding:11px 13px;background:#fff;
}
.pp-info-grid{display:grid;gap:10px 18px;}
.pp-cols-3{grid-template-columns:repeat(3,1fr);}
.pp-cols-4{grid-template-columns:repeat(4,1fr);}
.pp-info-item{display:flex;flex-direction:column;gap:2px;}
.pp-info-label{font-size:6.5pt;font-weight:700;color:#4a6fa5;text-transform:uppercase;letter-spacing:0.07em;}
.pp-info-val{
  font-size:9.5pt;font-weight:500;color:#184381;
  border-bottom:1px solid #c7d6ec;padding-bottom:3px;
}
.pp-info-val.highlight{color:#184381;font-weight:700;}
.pill-blue{display:inline-block;background:#dbeafe;color:#184381;padding:2px 9px;border-radius:20px;font-size:8pt;font-weight:700;}
.pill-green{display:inline-block;background:#e8f5e9;color:#2e7d32;padding:2px 9px;border-radius:20px;font-size:8pt;font-weight:700;}
.pill-gold{display:inline-block;background:#fff8e1;color:#7a5800;padding:2px 9px;border-radius:20px;font-size:8pt;font-weight:700;}
.pp-address-split{display:grid;grid-template-columns:1fr 1fr;gap:18px;}
.pp-addr-title{font-size:7.5pt;font-weight:700;color:#184381;letter-spacing:0.07em;text-transform:uppercase;margin-bottom:7px;padding-bottom:4px;border-bottom:1px dashed #c7d6ec;}
.pp-addr-row{display:grid;grid-template-columns:1fr 1fr;gap:10px;margin-top:8px;}
.pp-exam-table{width:100%;border-collapse:collapse;font-size:8.5pt;}
.pp-exam-table thead tr{background:#eaf1fb;}
.pp-exam-table th{text-align:left;padding:7px 10px;font-size:7pt;font-weight:700;color:#4a6fa5;text-transform:uppercase;letter-spacing:0.06em;border-bottom:2px solid #c7d6ec;}
.pp-exam-table td{padding:7px 10px;border-bottom:1px solid #c7d6ec;color:#184381;}
.pp-exam-table tr:last-child td{border-bottom:none;}
.pp-exam-table tbody tr:nth-child(even){background:#eaf1fb;}
.pp-declaration-box{
  border:1.5px solid #c8e6c9;border-left:4px solid #2e7d32;
  border-radius:5px;background:#e8f5e9;
  padding:10px 13px;font-size:9pt;color:#1b5e20;line-height:1.6;
}
.pp-sig-section{
  display:flex;justify-content:space-between;align-items:flex-end;
  margin-top:20px;padding-top:14px;border-top:1.5px dashed #c7d6ec;
  position:relative;z-index:1;
}
.pp-sig-block{text-align:center;width:150px;}
.pp-sig-line{border-top:1.5px solid #184381;margin-bottom:4px;margin-top:15px}
.pp-sig-block p{font-size:7.5pt;color:#4a6fa5;}
.pp-office-box{text-align:center;}
.pp-office-title{font-size:7pt;color:#6e93c9;margin-bottom:5px;}
.pp-office-fields{
  border:1px solid #c7d6ec;border-radius:6px;
  padding:8px 20px;font-size:8pt;color:#4a6fa5;
  display:flex;flex-direction:column;gap:4px;
}
.pp-doc-footer{
  margin-top:10px;padding-top:8px;border-top:1.5px solid #c7d6ec;
  display:flex;justify-content:space-between;
  font-size:7pt;color:#4a6fa5;position:relative;z-index:1;
}
.pp-doc-footer strong{color:#184381;}
`;

// ── Screen overlay + A4 preview wrapper styles ───────────────────────────
const printPreviewCSS = `
@import url('https://fonts.googleapis.com/css2?family=EB+Garamond:wght@400;600;700&family=DM+Sans:wght@300;400;500;600;700&display=swap');

.pp-overlay{
  position:fixed;inset:0;z-index:999999;
  background:#1a1a2e;display:flex;flex-direction:column;overflow:hidden;
}
.pp-toolbar{
  display:flex;align-items:center;justify-content:space-between;
  padding:10px 24px;background:#2b68c3;
  border-bottom:1px solid rgba(255,255,255,0.1);
  flex-shrink:0;gap:12px;flex-wrap:wrap;
}
.pp-toolbar-left{display:flex;flex-direction:column;gap:2px;}
.pp-toolbar-title{font-size:0.95rem;font-weight:700;color:#fff;}
.pp-toolbar-sub{font-size:0.72rem;color:rgba(255,255,255,0.5);}
.pp-toolbar-right{display:flex;gap:10px;align-items:center;}
.pp-close-btn{
  padding:8px 18px;background:rgba(255,255,255,0.1);
  border:1px solid rgba(255,255,255,0.2);
  color:#fff;border-radius:7px;cursor:pointer;font-size:0.8rem;transition:background 0.2s;
}
.pp-close-btn:hover{background:rgba(255,255,255,0.18);}
.pp-print-btn{
  padding:8px 22px;background:linear-gradient(135deg,#184381,#2b68c3);
  border:none;color:#fff;border-radius:7px;cursor:pointer;
  font-size:0.85rem;font-weight:700;
  box-shadow:0 3px 14px rgba(24,67,129,0.4);transition:filter 0.2s,transform 0.2s;
}
.pp-print-btn:hover{filter:brightness(1.1);transform:translateY(-1px);}
.pp-page-wrap{
min-height:0;
  flex:1;overflow-y:auto;overflow-x:auto;
  display:flex;justify-content:center;align-items:flex-start;
  padding:32px 20px 48px;background:#1a1a2e;
}
.pp-a4{
  width:794px;background:#fff;border-radius:4px;
  box-shadow:0 8px 48px rgba(0,0,0,0.6);
  padding:40px 44px 36px;
  font-family:'DM Sans',sans-serif;font-size:10pt;color:#184381;
  line-height:1.45;position:relative;
}

${printPreviewCSS_base}
`;

/* ── Main Component ────────────────────────────────────────────────────── */
export default function StudentRegistration() {
  // Student info from login (stored in localStorage by the login page)
  const studentInfo = JSON.parse(localStorage.getItem("student") || "{}");
  const student = studentInfo?.student || {};

  const [form, setForm] = useState({
    idProofType: "", idProofNo: "", idProofDoc: null,
    course: "", yearOfAdmission: "", neetRollNo: "",
    department: "", admissionCategory: "", otherCategory: "",
    bloodGroup: "",
    fatherName: "", fatherOccupation: "",
    passportPhoto: null,
    motherName: "", motherOccupation: "",
    reservationCategory: "", subCategory: "",
    signature: null,
    presentAddress: "", presentState: "", presentPin: "",
    sameAddress: false,
    permanentAddress: "", permanentState: "", permanentPin: "",
    parentMobile: "", parentEmail: "",
    examRows: [{ examination: "", yearOfPassing: "", college: "", board: "", remarks: "" }],
    declaration: false,
    captchaInput: "",
  });

  const [captcha, setCaptcha] = useState(generateCaptcha);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  // true only after the student clicks "Edit Details" on the thank-you screen
  const [editMode, setEditMode] = useState(false);

  // ── Registration state (from the DB on every fresh login) ──────────────
  //   status 2 + confirm 0 → Step 2 not submitted yet   → show the form
  //   status 2 + confirm 1 → Step 2 submitted, waiting for admin → "thank-you / pending" screen
  //   status 1             → admin confirmed (locked)   → "approved" screen
  //
  // The thank-you screen is shown whenever Step 2 was submitted (this session OR any
  // earlier login). The pre-filled edit form opens only when the student clicks
  // "Edit Details" (editMode), and they can resubmit until the admin confirms.
  const isApproved = Number(student.status) === 1;
  const hasSubmittedBefore = Number(student.status) === 2 && Number(student.confirm) === 1;
  const isPendingApproval = !isApproved && !editMode && (success || hasSubmittedBefore);

  // Files already stored on the server (filenames) when editing a submitted form
  const UPLOADS_URL = API_URL.replace(/\/api\/?$/, "") + "/uploads/students/";
  const [existingFiles, setExistingFiles] = useState({ idProofDoc: null, passportPhoto: null, signature: null });
  const [isEditing, setIsEditing] = useState(false);
  const [loadingDetails, setLoadingDetails] = useState(false);
  const fileUrl = (name) => (name ? UPLOADS_URL + name : null);

  // Course list for the dropdown (submits crs_id, displays course_name)
  const [courses, setCourses] = useState([]);
  useEffect(() => {
    fetch(`${API_URL}/student/courses`)
      .then(r => r.json())
      .then(d => { if (d.success) setCourses(d.courses || []); })
      .catch(() => {});
  }, []);
  const courseName = (id) => {
    const match = courses.find(c => String(c.crs_id) === String(id));
    return match ? match.course_name : (id || "—");
  };

  const odishaStates = ["Odisha", "Andhra Pradesh", "Arunachal Pradesh", "Assam", "Bihar", "Chhattisgarh", "Goa", "Gujarat", "Haryana", "Himachal Pradesh", "Jharkhand", "Karnataka", "Kerala", "Madhya Pradesh", "Maharashtra", "Manipur", "Meghalaya", "Mizoram", "Nagaland", "Punjab", "Rajasthan", "Sikkim", "Tamil Nadu", "Telangana", "Tripura", "Uttar Pradesh", "Uttarakhand", "West Bengal", "Delhi"];

  // ── Dynamic form options loaded from /student/form-options ──────────────
  // These were previously hardcoded in this page. The backend already exposes
  // them from the database, so the UI now uses the database values directly.
  const [formOptions, setFormOptions] = useState({
    idProofTypes: [],
    bloodGroups: [],
    reservationCategories: [],
    subCategories: [],
  });
  const [loadingFormOptions, setLoadingFormOptions] = useState(true);

  useEffect(() => {
    let cancelled = false;

    const loadFormOptions = async () => {
      setLoadingFormOptions(true);

      try {
        const res = await fetch(`${API_URL}/student/form-options`);
        const data = await res.json();

        if (!cancelled && data.success) {
          setFormOptions({
            idProofTypes: data.idProofTypes || [],
            bloodGroups: data.bloodGroups || [],
            reservationCategories: data.reservationCategories || [],
            subCategories: data.subCategories || [],
          });
        }
      } catch (err) {
        console.error("Failed to load dynamic form options:", err);
      } finally {
        if (!cancelled) setLoadingFormOptions(false);
      }
    };

    loadFormOptions();

    return () => {
      cancelled = true;
    };
  }, []);

  const ID_PROOF_TYPES = formOptions.idProofTypes;
  const BLOOD_GROUPS = formOptions.bloodGroups;
  const RESERVATION_CATEGORIES = formOptions.reservationCategories;
  const SUB_CATEGORIES = formOptions.subCategories;

  const idProofTypeLabel = (code) =>
    ID_PROOF_TYPES.find(t => String(t.value) === String(code))?.label || code || "—";

  const bloodGroupLabel = (code) =>
    BLOOD_GROUPS.find(b => String(b.value) === String(code))?.label || code || "—";

  const reservationCategoryLabel = (code) =>
    RESERVATION_CATEGORIES.find(r => String(r.value) === String(code))?.label || code || "—";

  const subCategoryLabel = (code) =>
    SUB_CATEGORIES.find(s => String(s.value) === String(code))?.label || code || "—";

  // ── Course → Department / Admission Category / Category cascade ─────────
  const [courseMeta, setCourseMeta] = useState({
    departments: [],
    showDepartment: false,
    admissionCategories: [],
    showAdmissionCategory: false,
    otherCategories: [],
    showOtherCategory: false,
    regdType: null,
  });

  const handleCourseChange = async (courseId) => {
    set("course", courseId);
    set("department", "");
    set("admissionCategory", "");
    set("otherCategory", "");

    if (!courseId) {
      setCourseMeta({
        departments: [], showDepartment: false,
        admissionCategories: [], showAdmissionCategory: false,
        otherCategories: [], showOtherCategory: false,
        regdType: null,
      });
      return;
    }

    try {
      const res = await fetch(`${API_URL}/student/course-meta/${courseId}`);
      const data = await res.json();
      if (data.success) {
        setCourseMeta({
          departments: data.departments || [],
          showDepartment: !!data.showDepartment,
          admissionCategories: data.admissionCategories || [],
          showAdmissionCategory: !!data.showAdmissionCategory,
          otherCategories: data.otherCategories || [],
          showOtherCategory: !!data.showOtherCategory,
          regdType: data.regdType,
        });
      }
    } catch (err) {
      console.error("Failed to load course dropdown data:", err);
    }
  };

  // ── Load the student's saved Step 2 data and pre-fill the form ──────────
  const loadDetails = async () => {
    setLoadingDetails(true);
    try {
      const res = await fetch(`${API_URL}/student/registration`, {
        headers: { Authorization: `Bearer ${studentInfo?.token}` },
      });
      const data = await res.json();
      if (!data.success || !data.data) return;
      const d = data.data;

      // Load the course-dependent dropdowns (department / categories)
      if (d.form.course) {
        try {
          const r = await fetch(`${API_URL}/student/course-meta/${d.form.course}`);
          const m = await r.json();
          if (m.success) {
            setCourseMeta({
              departments: m.departments || [],
              showDepartment: !!m.showDepartment,
              admissionCategories: m.admissionCategories || [],
              showAdmissionCategory: !!m.showAdmissionCategory,
              otherCategories: m.otherCategories || [],
              showOtherCategory: !!m.showOtherCategory,
              regdType: m.regdType,
            });
          }
        } catch (err) {
          console.error("Failed to load course dropdown data:", err);
        }
      }

      setForm(f => ({
        ...f,
        ...d.form,
        idProofDoc: null,
        passportPhoto: null,
        signature: null,
        declaration: false,   // student confirms the declaration again
        captchaInput: "",
      }));
      setExistingFiles(d.files || { idProofDoc: null, passportPhoto: null, signature: null });
      setIsEditing(true);
    } catch (err) {
      console.error("Failed to load saved registration:", err);
    } finally {
      setLoadingDetails(false);
    }
  };

  // NOTE: no auto-load on login any more. A student who already submitted Step 2
  // sees the thank-you screen; the pre-filled form opens only via "Edit Details".

  const [showPreview, setShowPreview] = useState(false);
  const [showPrintPreview, setShowPrintPreview] = useState(false);
  const refreshCaptcha = () => { setCaptcha(generateCaptcha()); setForm(f => ({ ...f, captchaInput: "" })); };

  const set = (key, val) => {
    setForm(f => {
      const next = { ...f, [key]: val };
      if (key === "sameAddress" && val) {
        next.permanentAddress = f.presentAddress;
        next.permanentState = f.presentState;
        next.permanentPin = f.presentPin;
      }
      return next;
    });
    if (errors[key]) setErrors(p => ({ ...p, [key]: undefined }));
  };

  // ── File validation rules (type + size) ─────────────────────────────────
  const FILE_RULES = {
    idProofDoc: {
      label: "ID Proof Document",
      types: { mimes: ["application/pdf", "image/jpeg", "image/jpg"], exts: ["pdf", "jpg", "jpeg"] },
      minKB: 0,
      maxKB: 200,
    },
    passportPhoto: {
      label: "Passport Photo",
      types: { mimes: ["image/jpeg", "image/jpg"], exts: ["jpg", "jpeg"] },
      minKB: 50,
      maxKB: 100,
    },
    signature: {
      label: "Signature",
      types: { mimes: ["image/jpeg", "image/jpg"], exts: ["jpg", "jpeg"] },
      minKB: 20,
      maxKB: 50,
    },
  };

  const handleFileSelect = (field, file) => {
    if (!file) {
      set(field, null);
      return;
    }
    const rule = FILE_RULES[field];
    const err = validateFile(file, rule);

    if (err) {
      setForm(f => ({ ...f, [field]: null })); // reject the file, don't store it
      setErrors(p => ({ ...p, [field]: err }));
      return;
    }

    setForm(f => ({ ...f, [field]: file }));
    setErrors(p => ({ ...p, [field]: undefined }));
  };

  const MAX_EXAM_ROWS = 2; // 1 initial row + 1 more allowed

  const addExamRow = () => {
    setForm(f => {
      if (f.examRows.length >= MAX_EXAM_ROWS) return f;
      return {
        ...f,
        examRows: [...f.examRows, { examination: "", yearOfPassing: "", college: "", board: "", remarks: "" }],
      };
    });
  };

  const removeExamRow = (idx) => {
    setForm(f => ({ ...f, examRows: f.examRows.filter((_, i) => i !== idx) }));
    setErrors(prev => {
      if (!prev.examRows) return prev;
      return { ...prev, examRows: prev.examRows.filter((_, i) => i !== idx) };
    });
  };

  const updateExamRow = (idx, field, val) => {
    setForm(f => {
      const rows = [...f.examRows];
      rows[idx] = { ...rows[idx], [field]: val };
      return { ...f, examRows: rows };
    });
    setErrors(prev => {
      if (!prev.examRows || !prev.examRows[idx] || !prev.examRows[idx][field]) return prev;
      const examRows = [...prev.examRows];
      const rowErr = { ...examRows[idx] };
      delete rowErr[field];
      examRows[idx] = rowErr;
      return { ...prev, examRows };
    });
  };

  const validate = () => {
    const e = {};
    if (!form.idProofType)     e.idProofType = "Required";
    if (!form.idProofNo)       e.idProofNo = "Required";
    if (!form.idProofDoc && !existingFiles.idProofDoc) e.idProofDoc = "Please attach ID proof";
    if (!form.course)          e.course = "Required";
    if (!form.yearOfAdmission) e.yearOfAdmission = "Required";
    // only required when the selected course actually shows these fields
    if (courseMeta.showDepartment && !form.department) e.department = "Required";
    if (courseMeta.showAdmissionCategory && !form.admissionCategory) e.admissionCategory = "Required";
    if (courseMeta.showOtherCategory && !form.otherCategory) e.otherCategory = "Required";
    if (!form.bloodGroup)      e.bloodGroup = "Required";
    if (!form.fatherName)      e.fatherName = "Required";
    if (!form.fatherOccupation)e.fatherOccupation = "Required";
    if (!form.passportPhoto && !existingFiles.passportPhoto) e.passportPhoto = "Required";
    if (!form.motherName)      e.motherName = "Required";
    if (!form.motherOccupation)e.motherOccupation = "Required";
    if (!form.reservationCategory) e.reservationCategory = "Required";
    if (!form.subCategory)     e.subCategory = "Required";
    if (!form.signature && !existingFiles.signature) e.signature = "Required";
    if (!form.presentAddress)  e.presentAddress = "Required";
    if (!form.presentState)    e.presentState = "Required";
    if (!form.presentPin)      e.presentPin = "Required";
    if (!form.permanentAddress)e.permanentAddress = "Required";
    if (!form.permanentState)  e.permanentState = "Required";
    if (!form.permanentPin)    e.permanentPin = "Required";
    if (!form.parentMobile)    e.parentMobile = "Required";
    if (!form.declaration)     e.declaration = "You must accept the declaration";
    if (!form.captchaInput)    e.captcha = "Please enter captcha";

    // ── Character limit checks ────────────────────────────────────
    const LIMITS = {
      idProofNo: 30,
      neetRollNo: 20,
      fatherName: 50,
      fatherOccupation: 50,
      motherName: 50,
      motherOccupation: 50,
      parentEmail: 50,
      presentAddress: 200,
      permanentAddress: 200,
    };

    Object.entries(LIMITS).forEach(([key, limit]) => {
      if (form[key] && form[key].length > limit) {
        e[key] = `Must be under ${limit} characters.`;
      }
    });

    const examErrors = form.examRows.map(row => {
      const re = {};
      if (!row.examination.trim())  re.examination = true;
      if (!row.yearOfPassing)       re.yearOfPassing = true;
      if (!row.college.trim())      re.college = true;
      if (!row.board.trim())        re.board = true;
      return re;
    });
    if (examErrors.some(re => Object.keys(re).length > 0)) {
      e.examRows = examErrors;
    }
    else if (form.captchaInput !== captcha) { e.captcha = "Captcha does not match. Try again."; refreshCaptcha(); }
    return e;
  };

  // ── Print: use cached data if it belongs to this student, otherwise rebuild
  //    it from the server (new login / new browser / different device) ───────
  const printRegistration = async () => {
    let data = JSON.parse(localStorage.getItem("studentRegistrationData") || "null");

    // Missing, or saved by a different student → rebuild from the server
    if (!data || data.student?.reg_id !== student.reg_id) {
      try {
        const res = await fetch(`${API_URL}/student/registration`, {
          headers: { Authorization: `Bearer ${studentInfo?.token}` },
        });
        const json = await res.json();
        if (!json.success || !json.data) {
          alert("Registration data not found.");
          return;
        }
        const d = json.data;

        let meta = { departments: [], admissionCategories: [], otherCategories: [] };
        if (d.form.course) {
          try {
            const m = await (await fetch(`${API_URL}/student/course-meta/${d.form.course}`)).json();
            if (m.success) meta = m;
          } catch (_) {}
        }

        data = {
          student,
          form: {
            ...d.form,
            passportPhoto: null, signature: null, idProofDoc: null,
            passportPhotoBase64: fileUrl(d.files.passportPhoto),
            signatureBase64: fileUrl(d.files.signature),
            idProofBase64: fileUrl(d.files.idProofDoc),
            departmentLabel: (meta.departments || []).find(x => String(x.dept_id) === String(d.form.department))?.dept_name || "",
            admissionCategoryLabel: (meta.admissionCategories || []).find(x => String(x.parid) === String(d.form.admissionCategory))?.par_value || "",
            otherCategoryLabel: (meta.otherCategories || []).find(x => String(x.parid) === String(d.form.otherCategory))?.par_value || "",
          },
        };
        localStorage.setItem("studentRegistrationData", JSON.stringify(data));
      } catch (err) {
        alert("Could not load registration data.");
        return;
      }
    }
    setShowPrintPreview(true);
  };

  const handlePrint = () => {
    const a4 = document.getElementById("pp-a4");
    if (!a4) return;

    const iframe = document.createElement("iframe");
    iframe.style.cssText = "position:fixed;top:-9999px;left:-9999px;width:794px;height:1123px;border:none;";
    document.body.appendChild(iframe);

    const doc = iframe.contentDocument || iframe.contentWindow.document;
    doc.open();
    doc.write(`<!DOCTYPE html>
  <html>
  <head>
  <meta charset="UTF-8"/>
  <link href="https://fonts.googleapis.com/css2?family=EB+Garamond:wght@400;600;700&family=DM+Sans:wght@300;400;500;600;700&display=swap" rel="stylesheet"/>
  <style>
    *,*::before,*::after{box-sizing:border-box;margin:0;padding:0;}
    @page{size:A4;margin:12mm 10mm;}
    body{
      font-family:'DM Sans',sans-serif;font-size:10pt;color:#184381;
      background:#fff;line-height:1.45;
      -webkit-print-color-adjust:exact;print-color-adjust:exact;
    }
    ${printPreviewCSS_base}
  </style>
  </head>
  <body>
  ${a4.innerHTML}
  </body>
  </html>`);
    doc.close();

    iframe.onload = () => {
      setTimeout(() => {
        iframe.contentWindow.focus();
        iframe.contentWindow.print();
        setTimeout(() => document.body.removeChild(iframe), 1000);
      }, 400);
    };
  };

  const handlePreview = (e) => {
    e.preventDefault();

    const errs = validate();

    if (Object.keys(errs).length) {
      setErrors(errs);
      window.scrollTo({ top: 0, behavior: "smooth" });
      return;
    }

    setShowPreview(true);
  };

  const submitRegistration = async () => {
    setLoading(true);

    try {
      const fd = new FormData();

      Object.entries(form).forEach(([k, v]) => {
        if (v === null) return; // file not replaced → server keeps the stored one
        if (v instanceof File) {
          fd.append(k, v);
        } else if (k === "examRows") {
          fd.append(k, JSON.stringify(v));
        } else {
          fd.append(k, v);
        }
      });

      const res = await fetch(
        `${API_URL}/student/registration`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${studentInfo?.token}`,
          },
          body: fd,
        }
      );

      const data = await res.json();

      if (data.success) {
        // Convert files to base64 for localStorage storage (used by the print preview)
        const toBase64 = (file) =>
          new Promise((resolve) => {
            if (!file) return resolve(null);
            const reader = new FileReader();
            reader.onload = () => resolve(reader.result);
            reader.readAsDataURL(file);
          });

        // When a file was not replaced, read the already-stored one from the server
        const storedToBase64 = async (name) => {
          if (!name) return null;
          try {
            const r = await fetch(UPLOADS_URL + name);
            return await toBase64(await r.blob());
          } catch (_) {
            return null;
          }
        };

        const [photoBase64, signatureBase64, idProofBase64] = await Promise.all([
          form.passportPhoto ? toBase64(form.passportPhoto) : storedToBase64(existingFiles.passportPhoto),
          form.signature ? toBase64(form.signature) : storedToBase64(existingFiles.signature),
          form.idProofDoc ? toBase64(form.idProofDoc) : storedToBase64(existingFiles.idProofDoc),
        ]);

        localStorage.setItem(
          "studentRegistrationData",
          JSON.stringify({
            student,
            form: {
              ...form,
              passportPhoto: null,        // File objects can't be serialized
              signature: null,
              idProofDoc: null,
              passportPhotoBase64: photoBase64,
              signatureBase64: signatureBase64,
              idProofBase64: idProofBase64,
              // resolved labels (form only stores raw ids) for the print preview
              idProofTypeLabel: idProofTypeLabel(form.idProofType),
              bloodGroupLabel: bloodGroupLabel(form.bloodGroup),
              reservationCategoryLabel: reservationCategoryLabel(form.reservationCategory),
              subCategoryLabel: subCategoryLabel(form.subCategory),
              departmentLabel: courseMeta.departments.find(d => String(d.dept_id) === String(form.department))?.dept_name || "",
              admissionCategoryLabel: courseMeta.admissionCategories.find(ac => String(ac.parid) === String(form.admissionCategory))?.par_value || "",
              otherCategoryLabel: courseMeta.otherCategories.find(ct => String(ct.parid) === String(form.otherCategory))?.par_value || "",
            },
          })
        );

        // Mark Step 2 as submitted in the cached login so a page refresh
        // still shows the thank-you screen instead of the empty form.
        const cached = JSON.parse(localStorage.getItem("student") || "{}");
        if (cached.student) {
          cached.student.confirm = 1;
          localStorage.setItem("student", JSON.stringify(cached));
        }

        setSuccess(true);
        setEditMode(false);     // back to the thank-you screen
        setIsEditing(false);
        setShowPreview(false);
      } else {
        setErrors({
          submit: data.message || "Submission failed"
        });
        setShowPreview(false);
        window.scrollTo({ top: 0, behavior: "smooth" });
      }

    } catch (err) {
      setErrors({
        submit: "Server error"
      });
      setShowPreview(false);
    }

    setLoading(false);
  };

  return (
    <>
      <style>{css}</style>
      <div className="stureg-page">
        {/* ───────────────── TOP NAVBAR ───────────────── */}
        <header className="ig-navbar">

          <div className="ig-navbar-left">

            <img
              className="logo-image"
              src={mkcgLogo}
              alt="MKCG Medical College"
            />

            <div className="ig-navbar-titles">

              <h1 className="ig-navbar-title">
                MKCG MEDICAL COLLEGE, BRAHMAPUR, ODISHA, INDIA
              </h1>

              <p className="ig-navbar-subtitle">
                ମହାରାଜା କୃଷ୍ଣଚନ୍ଦ୍ର ଗଜପତି ଭେଷଜ ମହାବିଦ୍ୟାଳୟ, ବ୍ରହ୍ମପୁର, ଓଡ଼ିଶା, ଭାରତ
              </p>

            </div>
          </div>

          <div className="ig-navbar-right">

            <span className="ig-navbar-badge">
              STUDENT INTERFACE
            </span>

            <Link
              to="/"
              className="ig-home-btn"
            >
              Return to Home
            </Link>

          </div>

        </header>

        {/* Main */}
        <main className="stureg-main">
          {isPendingApproval ? (
            /* ═══════════ PENDING SCREEN (status 2, confirm 1) ═══════════ */
            <div className="stureg-card stureg-success-card">

              {/* Animated check */}
              <div className="sc-check-wrap">
                <svg className="sc-check-svg" viewBox="0 0 52 52">
                  <circle className="sc-check-circle" cx="26" cy="26" r="25" fill="none" />
                  <path className="sc-check-tick" fill="none" d="M14 27l8 8 16-16" />
                </svg>
              </div>

              {/* Title */}
              <div className="sc-title-block">
                <h2 className="sc-title">Registration Form Submitted Successfully</h2>
              </div>

              {/* Divider */}
              <div className="sc-divider" />

              {/* Message */}
              <div className="sc-message-box">
                <div className="sc-message-icon">📋</div>
                <div className="sc-message-text">
                  <p>Dear <strong>{student.name || "Student"}</strong>,</p>
                  <p>
                    Thanks for submitting the Registration details. You can print a copy of
                    this registration form for reference purpose. You would get a
                    confirmation mail after the registration form is approved by the
                    Institution.
                  </p>
                </div>
              </div>

              {/* Actions */}
              <div className="sc-actions">
                <button className="sc-btn-print" onClick={printRegistration}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <polyline points="6 9 6 2 18 2 18 9"/><path d="M6 18H4a2 2 0 01-2-2v-5a2 2 0 012-2h16a2 2 0 012 2v5a2 2 0 01-2 2h-2"/>
                    <rect x="6" y="14" width="12" height="8"/>
                  </svg>
                  Print Registration Form
                </button>
                <button
                  className="sc-btn-print"
                  onClick={() => {
                    setSuccess(false);
                    setEditMode(true);
                    loadDetails();
                    window.scrollTo({ top: 0 });
                  }}
                >
                  ✎ Edit Details
                </button>
                <a href="/" className="sc-btn-dashboard">
                  Go to Home
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                    <line x1="5" y1="12" x2="19" y2="12"/><polyline points="12 5 19 12 12 19"/>
                  </svg>
                </a>
              </div>

              {/* Footer note */}
              {/* <p className="sc-footnote">
                Please contact the admission office if you need any correction.
              </p> */}

            </div>
          ) : isApproved ? (
            /* ═══════════ APPROVED SCREEN (status 1) ═══════════ */
            <div className="stureg-card stureg-success-card">

              {/* Animated check */}
              <div className="sc-check-wrap">
                <svg className="sc-check-svg" viewBox="0 0 52 52">
                  <circle className="sc-check-circle" cx="26" cy="26" r="25" fill="none" />
                  <path className="sc-check-tick" fill="none" d="M14 27l8 8 16-16" />
                </svg>
              </div>

              {/* Title */}
              <div className="sc-title-block">
                <h2 className="sc-title">Registration Confirmed</h2>
                {/* <div className="sc-ref">Registration ID: <span>{student.refNo || "—"}</span></div> */}
              </div>

              {/* Divider */}
              <div className="sc-divider" />

              {/* Message */}
              <div className="sc-message-box">
                <div className="sc-message-icon">🎓</div>
                <div className="sc-message-text">
                  <p>Welcome, <strong>{student.name}</strong>. Your registration has been reviewed and confirmed by <strong>M.K.C.G. Medical College, Brahmapur</strong>. Below is your profile on record.</p>
                  {/* <p>Below is your profile on record. Contact the admission office if anything needs to be corrected.</p> */}              
                </div>
              </div>

              {/* Profile summary */}
              <div className="stureg-info-strip" style={{ width: "100%", maxWidth: 640, borderRadius: 10, marginBottom: "1.5rem" }}>
                <div className="stureg-info-row" style={{ justifyContent: "center" }}>
                  {[
                    ["Student Name", student.name || "—"],
                    ["Gender", genderLabel(student.gender)],
                    ["DOB", formatDate(student.dob)],
                    ["Email", student.email || "—"],
                    ["Mobile", student.mobile || "—"],
                    ["Status", "Active"],
                  ].map(([k, v]) => (
                    <div key={k} className="stureg-info-item">
                      <span className="stureg-info-key">{k}:</span>
                      <span className="stureg-info-val">{v}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Actions */}
              <div className="sc-actions">
                <button className="sc-btn-print" onClick={printRegistration}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <polyline points="6 9 6 2 18 2 18 9"/><path d="M6 18H4a2 2 0 01-2-2v-5a2 2 0 012-2h16a2 2 0 012 2v5a2 2 0 01-2 2h-2"/>
                    <rect x="6" y="14" width="12" height="8"/>
                  </svg>
                  Print Registration Form
                </button>
                <a href="/" className="sc-btn-dashboard">
                  Go to Home
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                    <line x1="5" y1="12" x2="19" y2="12"/><polyline points="12 5 19 12 12 19"/>
                  </svg>
                </a>
              </div>

              {/* <p className="sc-footnote">
                Printing works from the device you originally submitted on. If it's unavailable here, contact the admission office for a copy.
              </p> */}

            </div>
          ) : (
            /* ═══════════ STEP 2 FORM (status 2, confirm 0) ═══════════ */
            <div className="stureg-card">
              {/* Card header */}
              <div className="stureg-card-header">
                <div className="stureg-card-header-text">
                  <span>Student Registration Form</span>
                  <span className="stureg-card-step">
                    {isEditing ? "(Step 2 — Edit your submitted details)" : "(Step 2 of Registration Process)"}
                  </span>
                </div>
                <div className="stureg-status-badge">
                  ● Status:{" "}
                  <span className="stureg-incomplete">{isEditing ? "Submitted – Pending Approval" : "Incomplete"}</span>
                </div>
              </div>

              {/* Student Info strip */}
              <div className="stureg-info-strip">
                <div className="stureg-info-hint">
                  {isEditing
                    ? "Your submitted details are shown below. You can edit them and submit again until the Institution approves your registration."
                    : "Please upload your remaining details and documents to complete the process."}
                </div>
                {loadingDetails && <div className="stureg-info-hint">Loading your saved details…</div>}
                <div className="stureg-info-row">
                  {[
                    ["Student Name", student.name || "—"],
                    ["Gender", genderLabel(student.gender)],
                    ["DOB", formatDate(student.dob)],
                    ["Email", student.email || "—"],
                    ["Mobile", student.mobile || "—"],
                  ].map(([k, v]) => (
                    <div key={k} className="stureg-info-item">
                      <span className="stureg-info-key">{k}:</span>
                      <span className="stureg-info-val">{v}</span>
                    </div>
                  ))}
                </div>
              </div>

              {errors.submit && <div className="stureg-submit-error">⚠ {errors.submit}</div>}

              <form onSubmit={handlePreview} noValidate className="stureg-form">

                {/* ── ID Proof ── */}
                <div className="stureg-grid-3">
                  <Field label="ID Proof Type" required error={errors.idProofType}>
                    <Select
                      error={errors.idProofType}
                      value={form.idProofType}
                      onChange={e => set("idProofType", e.target.value)}
                      disabled={loadingFormOptions}
                    >
                      <option value="">-- Select --</option>
                      {ID_PROOF_TYPES.map(t => (
                        <option key={t.value} value={t.value}>
                          {t.label}
                        </option>
                      ))}
                    </Select>
                  </Field>

                  <Field label="ID Proof No" required error={errors.idProofNo}>
                    <Input
                      error={errors.idProofNo}
                      placeholder="Id Proof No"
                      maxLength={30}
                      value={form.idProofNo}
                      onChange={e => set("idProofNo", e.target.value)}
                    />
                  </Field>

                  <Field label="Year of Admission" required error={errors.yearOfAdmission}>
                    <Select
                      error={errors.yearOfAdmission}
                      value={form.yearOfAdmission}
                      onChange={e => set("yearOfAdmission", e.target.value)}
                    >
                      <option value="">-- Select --</option>
                      {Array.from(
                        { length: 10 },
                        (_, i) => new Date().getFullYear() - i
                      ).map(year => (
                        <option key={year} value={year}>
                          {year}
                        </option>
                      ))}
                    </Select>
                  </Field>
                </div>

                {/* ── Course / Admission ── */}
                <div className="stureg-grid-3">
                  <Field label="Course" required error={errors.course}>
                    <Select
                      error={errors.course}
                      value={form.course}
                      onChange={e => handleCourseChange(e.target.value)}
                    >
                      <option value="">-- Select --</option>
                      {courses.map(c => (
                        <option key={c.crs_id} value={c.crs_id}>
                          {c.course_name}
                        </option>
                      ))}
                    </Select>
                  </Field>

                  <Field
                    label={
                      courseMeta.regdType !== null
                        ? (String(courseMeta.regdType) === "1" ? "NEET Roll No" : "Registration No")
                        : "NEET Roll No. / Registration No."
                    }
                    error={errors.neetRollNo}
                  >
                    <Input
                      error={errors.neetRollNo}
                      placeholder="NEET Roll No/ Regd. No"
                      maxLength={20}
                      value={form.neetRollNo}
                      onChange={e => set("neetRollNo", e.target.value)}
                    />
                  </Field>

                  <Field label="Blood Group" required error={errors.bloodGroup}>
                    <Select
                      error={errors.bloodGroup}
                      value={form.bloodGroup}
                      onChange={e => set("bloodGroup", e.target.value)}
                      disabled={loadingFormOptions}
                    >
                      <option value="">-- Select --</option>
                      {BLOOD_GROUPS.map(b => (
                        <option key={b.value} value={b.value}>{b.label}</option>
                      ))}
                    </Select>
                  </Field>
                </div>

                {/* ── Department / Admission Category / Category (shown per selected Course) ── */}
                {(courseMeta.showDepartment || courseMeta.showAdmissionCategory || courseMeta.showOtherCategory) && (
                  <div className="stureg-grid-3">
                    {courseMeta.showDepartment && (
                      <Field label="Department Name" required error={errors.department}>
                        <Select
                          error={errors.department}
                          value={form.department}
                          onChange={e => set("department", e.target.value)}
                        >
                          <option value="">-- Select --</option>
                          {courseMeta.departments.map(d => (
                            <option key={d.dept_id} value={d.dept_id}>{d.dept_name}</option>
                          ))}
                        </Select>
                      </Field>
                    )}

                    {courseMeta.showAdmissionCategory && (
                      <Field label="Admission Category" required error={errors.admissionCategory}>
                        <Select
                          error={errors.admissionCategory}
                          value={form.admissionCategory}
                          onChange={e => set("admissionCategory", e.target.value)}
                        >
                          <option value="">-- Select --</option>
                          {courseMeta.admissionCategories.map(ac => (
                            <option key={ac.parid} value={ac.parid}>{ac.par_value}</option>
                          ))}
                        </Select>
                      </Field>
                    )}

                    {courseMeta.showOtherCategory && (
                      <Field label="Category" required error={errors.otherCategory}>
                        <Select
                          error={errors.otherCategory}
                          value={form.otherCategory}
                          onChange={e => set("otherCategory", e.target.value)}
                        >
                          <option value="">-- Select --</option>
                          {courseMeta.otherCategories.map(ct => (
                            <option key={ct.parid} value={ct.parid}>{ct.par_value}</option>
                          ))}
                        </Select>
                      </Field>
                    )}
                  </div>
                )}

                {/* ── Personal Details ── */}
                <div className="stureg-grid-3">
                  <Field label="Father's Name" required error={errors.fatherName}>
                    <Input
                      error={errors.fatherName}
                      placeholder="Father's Name"
                      maxLength={50}
                      value={form.fatherName}
                      onChange={e => set("fatherName", e.target.value)}
                    />
                  </Field>

                  <Field label="Father's Occupation" required error={errors.fatherOccupation}>
                    <Input
                      error={errors.fatherOccupation}
                      placeholder="Father's Occupation"
                      maxLength={50}
                      value={form.fatherOccupation}
                      onChange={e => set("fatherOccupation", e.target.value)}
                    />
                  </Field>

                  <Field label="Mother's Name" required error={errors.motherName}>
                    <Input
                      error={errors.motherName}
                      placeholder="Mother's Name"
                      maxLength={50}
                      value={form.motherName}
                      onChange={e => set("motherName", e.target.value)}
                    />
                  </Field>
                </div>

                <div className="stureg-grid-3">
                  <Field label="Mother's Occupation" required error={errors.motherOccupation}>
                    <Input
                      error={errors.motherOccupation}
                      placeholder="Mother's Occupation"
                      maxLength={50}
                      value={form.motherOccupation}
                      onChange={e => set("motherOccupation", e.target.value)}
                    />
                  </Field>

                  <Field label="Reservation Category" required error={errors.reservationCategory}>
                    <Select
                      error={errors.reservationCategory}
                      value={form.reservationCategory}
                      onChange={e => set("reservationCategory", e.target.value)}
                      disabled={loadingFormOptions}
                    >
                      <option value="">-- Select --</option>
                      {RESERVATION_CATEGORIES.map(r => (
                        <option key={r.value} value={r.value}>{r.label}</option>
                      ))}
                    </Select>
                  </Field>

                  <Field label="Sub Category" required error={errors.subCategory}>
                    <Select
                      error={errors.subCategory}
                      value={form.subCategory}
                      onChange={e => set("subCategory", e.target.value)}
                      disabled={loadingFormOptions}
                    >
                      <option value="">-- Select --</option>
                      {SUB_CATEGORIES.map(s => (
                        <option key={s.value} value={s.value}>{s.label}</option>
                      ))}
                    </Select>
                  </Field>
                </div>

                {/* ── Upload Documents ── */}
                <div className="stureg-grid-3">
                  <Field label="Attachment of ID Proof Document" required error={errors.idProofDoc}>
                    <FileInput
                      hint="(PDF/JPEG/JPG; max 200KB)"
                      error={errors.idProofDoc}
                      value={form.idProofDoc}
                      existingUrl={fileUrl(existingFiles.idProofDoc)}
                      existingName={existingFiles.idProofDoc}
                      onChange={e => handleFileSelect("idProofDoc", e.target.files[0])}
                    />
                  </Field>

                  <Field label="Attach Passport Photo of Student" required error={errors.passportPhoto}>
                    <FileInput
                      hint="(JPEG/JPG format, size 50-100KB)"
                      error={errors.passportPhoto}
                      value={form.passportPhoto}
                      existingUrl={fileUrl(existingFiles.passportPhoto)}
                      existingName={existingFiles.passportPhoto}
                      onChange={e => handleFileSelect("passportPhoto", e.target.files[0])}
                    />
                  </Field>

                  <Field label="Attach Signature" required error={errors.signature}>
                    <FileInput
                      hint="(JPEG/JPG format, size 20-50KB)"
                      error={errors.signature}
                      value={form.signature}
                      existingUrl={fileUrl(existingFiles.signature)}
                      existingName={existingFiles.signature}
                      onChange={e => handleFileSelect("signature", e.target.files[0])}
                    />
                  </Field>
                </div>

                {/* ── Present Address ── */}
                <SectionHeading>Present Address</SectionHeading>
                <div className="stureg-grid-3">
                  <Field label="Address" required error={errors.presentAddress}>
                    <div className={`stureg-input-wrap stureg-textarea-wrap${errors.presentAddress ? " stureg-err" : ""}`}>
                      <textarea placeholder="Present address" rows={2} maxLength={200} value={form.presentAddress} onChange={e => set("presentAddress", e.target.value)} />
                    </div>
                  </Field>
                  <Field label="State" required error={errors.presentState}>
                    <Select error={errors.presentState} value={form.presentState} onChange={e => set("presentState", e.target.value)}>
                      <option value="">-- Select --</option>
                      {odishaStates.map(s => <option key={s}>{s}</option>)}
                    </Select>
                  </Field>
                  <Field label="Pin" required error={errors.presentPin}>
                    <Input error={errors.presentPin} placeholder="Pin Code" maxLength={6} value={form.presentPin} onChange={e => set("presentPin", e.target.value.replace(/\D/, ""))} />
                  </Field>
                </div>

                {/* ── Permanent Address ── */}
                <SectionHeading>
                  Permanent Address
                  <label className="stureg-same-check">
                    <input type="checkbox" checked={form.sameAddress} onChange={e => set("sameAddress", e.target.checked)} />
                    <span>Same as present address</span>
                  </label>
                </SectionHeading>
                <div className="stureg-grid-3">
                  <Field label="Address" required error={errors.permanentAddress}>
                    <div className={`stureg-input-wrap stureg-textarea-wrap${errors.permanentAddress ? " stureg-err" : ""}`}>
                      <textarea placeholder="Permanent Address" rows={2} maxLength={200} value={form.permanentAddress} disabled={form.sameAddress} onChange={e => set("permanentAddress", e.target.value)} />
                    </div>
                  </Field>
                  <Field label="State" required error={errors.permanentState}>
                    <Select error={errors.permanentState} value={form.permanentState} disabled={form.sameAddress} onChange={e => set("permanentState", e.target.value)}>
                      <option value="">-- Select --</option>
                      {odishaStates.map(s => <option key={s}>{s}</option>)}
                    </Select>
                  </Field>
                  <Field label="Pin" required error={errors.permanentPin}>
                    <Input error={errors.permanentPin} placeholder="Pin Code" maxLength={6} value={form.permanentPin} disabled={form.sameAddress} onChange={e => set("permanentPin", e.target.value.replace(/\D/, ""))} />
                  </Field>
                </div>

                {/* ── Contact Details ── */}
                <SectionHeading>Contact Details of Father/Mother</SectionHeading>
                <div className="stureg-grid-2">
                  <Field label="Mobile Number" required error={errors.parentMobile}>
                    <Input error={errors.parentMobile} placeholder="Mobile number" maxLength={10} value={form.parentMobile} onChange={e => set("parentMobile", e.target.value.replace(/\D/, ""))} />
                  </Field>
                  <Field label="Email ID" error={errors.parentEmail}>
                    <Input placeholder="Email id" maxLength={50} value={form.parentEmail} onChange={e => set("parentEmail", e.target.value)} />
                  </Field>
                </div>

                {/* ── Exam Details ── */}
                <SectionHeading>Last Qualifying Examination Details Of Student</SectionHeading>
                <div className="stureg-exam-table">
                  <div className="stureg-exam-header">
                    <span>Examination <span className="stureg-req">*</span></span>
                    <span>Year of Passing <span className="stureg-req">*</span></span>
                    <span>Name of College <span className="stureg-req">*</span></span>
                    <span>Board/University <span className="stureg-req">*</span></span>
                    <span>Remarks </span>
                    <span></span>
                  </div>

                  {form.examRows.map((row, idx) => (
                    <ExamRow
                      key={idx}
                      row={row}
                      idx={idx}
                      onChange={updateExamRow}
                      onRemove={removeExamRow}
                      onAdd={form.examRows.length < MAX_EXAM_ROWS ? addExamRow : undefined}
                      canRemove={idx > 0}
                      rowErrors={errors.examRows?.[idx] || {}}
                    />
                  ))}
                  {form.examRows.length < MAX_EXAM_ROWS && (
                    <button type="button" className="stureg-add-row-btn" onClick={addExamRow}>
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
                      Add Another Examination
                    </button>
                  )}
                  {errors.examRows && (
                    <p className="stureg-error-msg" style={{ marginTop: 6 }}>
                      ⚠ Please fill in Examination, Year of Passing, College and Board/University for every row.
                    </p>
                  )}
                </div>

                {/* ── Declaration ── */}
                <div className="stureg-declaration">
                  <label className="stureg-decl-label">
                    <input type="checkbox" checked={form.declaration} onChange={e => set("declaration", e.target.checked)} />
                    <span>I hereby declare that all the information provided by me in this application is correct to the best of my knowledge and belief.</span>
                    <span className="stureg-req"> *</span>
                  </label>
                  {errors.declaration && <p className="stureg-error-msg" style={{marginTop:6}}>⚠ {errors.declaration}</p>}
                </div>

                {/* ── Captcha ── */}
                <div className="stureg-field stureg-captcha-section">
                  <label className="stureg-label">Captcha <span className="stureg-req">*</span></label>
                  <div className="stureg-captcha-row">
                    <CaptchaCanvas text={captcha} />
                    <button type="button" className="stureg-refresh-btn" onClick={refreshCaptcha} title="Refresh captcha">
                      <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                        <polyline points="23 4 23 10 17 10"/>
                        <path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10"/>
                      </svg>
                    </button>
                    <div className={`stureg-input-wrap stureg-captcha-input${errors.captcha ? " stureg-err" : ""}`}>
                      <input type="text" placeholder="Enter Captcha" value={form.captchaInput} onChange={e => { set("captchaInput", e.target.value); if (errors.captcha) setErrors(p => ({ ...p, captcha: undefined })); }} autoComplete="off" />
                    </div>
                  </div>
                  {errors.captcha && <p className="stureg-error-msg">⚠ {errors.captcha}</p>}
                </div>

                {/* ── Submit ── */}
                <div className="stureg-submit-row">
                  <button
                    type="button"
                    className="stureg-submit-btn"
                    onClick={handlePreview}
                  >
                    Preview Details →
                  </button>
                </div>

              </form>
            </div>
          )}

          {/* ═══════════ CONFIRM / PREVIEW MODAL ═══════════ */}
          {showPreview && (
            <div className="preview-overlay">
              <div className="preview-modal">

                <div className="preview-header">
                  <button
                    className="preview-close"
                    onClick={() => setShowPreview(false)}
                  >
                    ✕
                  </button>
                  <h2>Confirm Student Registration Details</h2>
                  <p className="preview-header-sub">You need to confirm the registration form</p>
                </div>

                {/* Images */}
                <div className="preview-images">

                  <div className="preview-image-card">
                    <h4>Student Photo</h4>
                    {(form.passportPhoto || existingFiles.passportPhoto) && (
                      <img
                        src={form.passportPhoto ? URL.createObjectURL(form.passportPhoto) : fileUrl(existingFiles.passportPhoto)}
                        alt=""
                      />
                    )}
                  </div>

                  <div className="preview-image-card">
                    <h4>Signature</h4>
                    {(form.signature || existingFiles.signature) && (
                      <img
                        src={form.signature ? URL.createObjectURL(form.signature) : fileUrl(existingFiles.signature)}
                        alt=""
                      />
                    )}
                  </div>

                  <div className="preview-image-card">
                    <h4>ID Proof</h4>
                    {!form.idProofDoc && existingFiles.idProofDoc && (
                      /\.pdf$/i.test(existingFiles.idProofDoc) ? (
                        <a href={fileUrl(existingFiles.idProofDoc)} target="_blank" rel="noreferrer">
                          📄 {existingFiles.idProofDoc}
                        </a>
                      ) : (
                        <img src={fileUrl(existingFiles.idProofDoc)} alt="" />
                      )
                    )}
                    {form.idProofDoc &&
                      form.idProofDoc.type.includes("image") && (
                        <img
                          src={URL.createObjectURL(form.idProofDoc)}
                          alt=""
                        />
                      )}

                    {form.idProofDoc &&
                      form.idProofDoc.type.includes("pdf") && (
                        <div
                          className="pdf-file"
                          role="button"
                          tabIndex={0}
                          onClick={() => window.open(URL.createObjectURL(form.idProofDoc), "_blank")}
                          onKeyDown={(e) => {
                            if (e.key === "Enter" || e.key === " ") {
                              window.open(URL.createObjectURL(form.idProofDoc), "_blank");
                            }
                          }}
                          style={{ cursor: "pointer", color: "blue" }}
                          title="Click to view PDF"
                        >
                          📄 {form.idProofDoc.name}
                        </div>
                      )}
                  </div>

                </div>

                {/* Student Details */}
                <div className="preview-section">
                  <h3>Student Information</h3>

                  <div className="preview-grid">
                    <div><label>Name</label><span>{student.name}</span></div>
                    <div><label>Email</label><span>{student.email}</span></div>
                    <div><label>Mobile</label><span>{student.mobile}</span></div>
                    <div><label>Gender</label><span>{genderLabel(student.gender)}</span></div>
                    <div><label>DOB</label><span>{formatDate(student.dob)}</span></div>
                    <div><label>Blood Group</label><span>{bloodGroupLabel(form.bloodGroup)}</span></div>
                  </div>
                </div>

                {/* Academic */}
                <div className="preview-section">
                  <h3>Academic Information</h3>

                  <div className="preview-grid">
                    <div><label>Course</label><span>{courseName(form.course)}</span></div>
                    <div><label>Admission Year</label><span>{form.yearOfAdmission}</span></div>
                    <div><label>NEET Roll No</label><span>{form.neetRollNo}</span></div>
                    <div><label>ID Proof Type</label><span>{idProofTypeLabel(form.idProofType)}</span></div>
                    <div><label>ID Proof No</label><span>{form.idProofNo}</span></div>
                    <div><label>Category</label><span>{reservationCategoryLabel(form.reservationCategory)}</span></div>
                    <div><label>Sub Category</label><span>{subCategoryLabel(form.subCategory)}</span></div>
                    {courseMeta.showDepartment && (
                      <div><label>Department</label><span>{courseMeta.departments.find(d => String(d.dept_id) === String(form.department))?.dept_name || "—"}</span></div>
                    )}
                    {courseMeta.showAdmissionCategory && (
                      <div><label>Admission Category</label><span>{courseMeta.admissionCategories.find(ac => String(ac.parid) === String(form.admissionCategory))?.par_value || "—"}</span></div>
                    )}
                    {courseMeta.showOtherCategory && (
                      <div><label>Other Category</label><span>{courseMeta.otherCategories.find(ct => String(ct.parid) === String(form.otherCategory))?.par_value || "—"}</span></div>
                    )}
                  </div>
                </div>

                {/* Parents */}
                <div className="preview-section">
                  <h3>Parent Information</h3>

                  <div className="preview-grid">
                    <div><label>Father Name</label><span>{form.fatherName}</span></div>
                    <div><label>Father Occupation</label><span>{form.fatherOccupation}</span></div>

                    <div><label>Mother Name</label><span>{form.motherName}</span></div>
                    <div><label>Mother Occupation</label><span>{form.motherOccupation}</span></div>

                    <div><label>Parent Mobile</label><span>{form.parentMobile}</span></div>
                    <div><label>Parent Email</label><span>{form.parentEmail}</span></div>
                  </div>
                </div>

                {/* Address */}
                <div className="preview-section">
                  <h3>Address Information</h3>

                  <div className="preview-grid">
                    <div>
                      <label>Present Address</label>
                      <span>{form.presentAddress}</span>
                    </div>

                    <div>
                      <label>Present State</label>
                      <span>{form.presentState}</span>
                    </div>

                    <div>
                      <label>Present PIN</label>
                      <span>{form.presentPin}</span>
                    </div>
                    <br />
                    <div>
                      <label>Permanent Address</label>
                      <span>{form.permanentAddress}</span>
                    </div>

                    <div>
                      <label>Permanent State</label>
                      <span>{form.permanentState}</span>
                    </div>

                    <div>
                      <label>Permanent PIN</label>
                      <span>{form.permanentPin}</span>
                    </div>
                  </div>
                </div>

                {/* Exams */}
                <div className="preview-section">
                  <h3>Examination Details</h3>

                  <table className="preview-table">
                    <thead>
                      <tr>
                        <th>Exam</th>
                        <th>Year</th>
                        <th>College</th>
                        <th>Board</th>
                        <th>Remarks</th>
                      </tr>
                    </thead>

                    <tbody>
                      {form.examRows.map((row, i) => (
                        <tr key={i}>
                          <td>{row.examination}</td>
                          <td>{row.yearOfPassing}</td>
                          <td>{row.college}</td>
                          <td>{row.board}</td>
                          <td>{row.remarks}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                <div className="preview-footer">
                  <button
                    className="preview-edit-btn"
                    onClick={() => setShowPreview(false)}
                  >
                    Edit Details
                  </button>

                  <button
                    className="preview-submit-btn"
                    onClick={submitRegistration}
                    disabled={loading}
                  >
                    {loading ? "Submitting..." : "Confirm & Submit"}
                  </button>
                </div>

              </div>
            </div>
          )}

          {/* ═══════════ PRINT PREVIEW ═══════════ */}
          {showPrintPreview && (() => {
            const data = JSON.parse(localStorage.getItem("studentRegistrationData") || "{}");
            const pd = data?.student || {};
            const pf = data?.form || {};

            return (
              <div className="pp-overlay" id="pp-overlay">
                <style>{printPreviewCSS}</style>

                {/* Toolbar — hidden on print */}
                <div className="pp-toolbar no-print">
                  <div className="pp-toolbar-left">
                    <span className="pp-toolbar-title">📄 Print Preview</span>
                    <span className="pp-toolbar-sub">M.K.C.G. Medical College — Student Registration</span>
                  </div>
                  <div className="pp-toolbar-right">
                    <button className="pp-close-btn" onClick={() => setShowPrintPreview(false)}>✕ Close</button>
                    <button className="pp-print-btn" onClick={handlePrint}>🖨 Print / Save PDF</button>
                  </div>
                </div>

                {/* A4 Document */}
                <div className="pp-page-wrap">
                  <div className="pp-a4" id="pp-a4">

                    {/* Watermark */}
                    <div className="pp-watermark">MKCG</div>

                    {/* Header */}
                    <div className="pp-header">
                      <img
                        className="pp-logo"
                        src={mkcgLogo}
                        alt="MKCG Logo"
                      />
                      <div className="pp-header-mid">
                        <div className="pp-college-name">M.K.C.G. Medical College</div>
                        <div className="pp-college-sub">Brahmapur, Odisha, India</div>
                        <div className="pp-college-odia">ମହାରାଜା କୃଷ୍ଣଚନ୍ଦ୍ର ଗଜପତି ଭେଷଜ ମହାବିଦ୍ୟାଳୟ, ବ୍ରହ୍ମପୁର, ଓଡ଼ିଶା, ଭାରତ</div>
                      </div>
                      <div className="pp-header-right">
                        <div className="pp-print-date">
                          Printed: {new Date().toLocaleDateString("en-IN", { dateStyle: "long" })}
                        </div>
                      </div>
                    </div>

                    {/* Title bar */}
                    <div className="pp-title-bar">
                      <div className="pp-title-main">Student Registration Form</div>
                    </div>

                    {/* Student banner */}
                    <div className="pp-student-banner">
                      <div className="pp-photo-box">
                        {pf.passportPhotoBase64 ? (
                          <img
                            src={pf.passportPhotoBase64}
                            alt="Passport"
                            style={{ width: "100%", height: "100%", objectFit: "cover" }}
                          />
                        ) : (
                          <span className="pp-photo-placeholder">📷<br/>Passport<br/>Photo</span>
                        )}
                      </div>
                      <div className="pp-student-grid">
                        {[
                          ["Full Name", pd.name],
                          ["Email Address", pd.email],
                          ["Mobile", pd.mobile],
                          ["Gender", genderLabel(pd.gender)],
                          ["Date of Birth", formatDate(pd.dob)],
                          ["Blood Group", bloodGroupLabel(pf.bloodGroup)],
                        ].map(([label, val]) => (
                          <div key={label} className="pp-si-item">
                            <span className="pp-si-label">{label}</span>
                            <span className={`pp-si-val${label === "Blood Group" ? " pill-blue" : ""}`}>{val || "—"}</span>
                          </div>
                        ))}
                      </div>
                      <div className="pp-sig-box">
                        <div className="pp-sig-img-box">
                          {pf.signatureBase64 ? (
                            <img
                              src={pf.signatureBase64}
                              alt="Signature"
                              style={{ width: "100%", height: "100%", objectFit: "contain" }}
                            />
                          ) : (
                            <span className="pp-sig-placeholder">Signature</span>
                          )}
                        </div>
                        <span className="pp-sig-label">Student Signature</span>
                      </div>
                    </div>

                    {/* Section renderer */}
                    {[
                      {
                        icon: "🎓", title: "Academic Information",
                        cols: 4,
                        items: [
                          ["Course", courseName(pf.course), "highlight"],
                          ["Year of Admission", pf.yearOfAdmission],
                          ["NEET Roll No. / Reg. No.", pf.neetRollNo],
                          ["Reservation Category", pf.reservationCategoryLabel || reservationCategoryLabel(pf.reservationCategory), "pill-gold"],
                          ["Sub Category", pf.subCategoryLabel || subCategoryLabel(pf.subCategory)],
                          ...(pf.departmentLabel ? [["Department", pf.departmentLabel]] : []),
                          ...(pf.admissionCategoryLabel ? [["Admission Category", pf.admissionCategoryLabel, "pill-gold"]] : []),
                          ...(pf.otherCategoryLabel ? [["Category", pf.otherCategoryLabel]] : []),
                        ]
                      },
                      {
                        icon: "🆔", title: "Identity Proof",
                        cols: 3,
                        items: [
                          ["ID Proof Type", pf.idProofTypeLabel || idProofTypeLabel(pf.idProofType)],
                          ["ID Proof Number", pf.idProofNo, "highlight"],
                          ["Document Status", "✓ Document Attached", "pill-green"],
                        ]
                      },
                      {
                        icon: "👨‍👩‍👦", title: "Family Information",
                        cols: 3,
                        items: [
                          ["Father's Name", pf.fatherName],
                          ["Father's Occupation", pf.fatherOccupation],
                          ["Parent Mobile", pf.parentMobile],
                          ["Mother's Name", pf.motherName],
                          ["Mother's Occupation", pf.motherOccupation],
                          ["Parent Email", pf.parentEmail],
                        ]
                      },
                    ].map(sec => (
                      <div key={sec.title} className="pp-section">
                        <div className="pp-sec-head">{sec.icon}&nbsp; {sec.title}</div>
                        <div className="pp-sec-body">
                          <div className={`pp-info-grid pp-cols-${sec.cols}`}>
                            {sec.items.map(([label, val, cls]) => (
                              <div key={label} className="pp-info-item">
                                <span className="pp-info-label">{label}</span>
                                <span className={`pp-info-val ${cls || ""}`}>{val || "—"}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                    ))}

                    {/* Address */}
                    <div className="pp-section">
                      <div className="pp-sec-head">📍&nbsp; Address Details</div>
                      <div className="pp-sec-body">
                        <div className="pp-address-split">
                          {[
                            ["Present Address", pf.presentAddress, pf.presentState, pf.presentPin],
                            ["Permanent Address", pf.permanentAddress, pf.permanentState, pf.permanentPin],
                          ].map(([title, addr, state, pin]) => (
                            <div key={title}>
                              <div className="pp-addr-title">{title}</div>
                              <div className="pp-info-item"><span className="pp-info-label">Address</span><span className="pp-info-val">{addr || "—"}</span></div>
                              <div className="pp-addr-row">
                                <div className="pp-info-item"><span className="pp-info-label">State</span><span className="pp-info-val">{state || "—"}</span></div>
                                <div className="pp-info-item"><span className="pp-info-label">PIN Code</span><span className="pp-info-val">{pin || "—"}</span></div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>

                    {/* Exam table */}
                    <div className="pp-section">
                      <div className="pp-sec-head">📄&nbsp; Last Qualifying Examination Details</div>
                      <div className="pp-sec-body" style={{ padding: 0 }}>
                        <table className="pp-exam-table">
                          <thead>
                            <tr>
                              {["#", "Examination", "Year of Passing", "Name of College", "Board / University", "Remarks"].map(h => (
                                <th key={h}>{h}</th>
                              ))}
                            </tr>
                          </thead>
                          <tbody>
                            {(pf.examRows || []).length > 0 ? pf.examRows.map((row, i) => (
                              <tr key={i}>
                                <td>{i + 1}</td>
                                <td>{row.examination || "—"}</td>
                                <td>{row.yearOfPassing || "—"}</td>
                                <td>{row.college || "—"}</td>
                                <td>{row.board || "—"}</td>
                                <td>{row.remarks || "—"}</td>
                              </tr>
                            )) : (
                              <tr><td colSpan={6} style={{ textAlign: "center", color: "#6e93c9", padding: "14px" }}>No examination records added</td></tr>
                            )}
                          </tbody>
                        </table>
                      </div>
                    </div>

                    {/* Signature row */}
                    <div className="pp-sig-section">
                      <div className="pp-sig-block">
                        <div className="pp-sig-line" />
                        <p>Signature of Parents</p>
                      </div>
                      <div className="pp-sig-block">
                        <div className="pp-sig-line" />
                        <p>Signature of Student</p>
                      </div>
                    </div>

                    {/* Doc footer */}
                    <div className="pp-doc-footer">
                      <span>
                        All Rights Reserved © MKCG Medical College, Brahmapur, Odisha —{" "}
                        {new Date().getFullYear()}
                      </span>
                      <span>Powered by <strong>Orisys Infotech</strong></span>
                    </div>

                  </div>
                </div>
              </div>
            );
          })()}

        </main>

        {/* Footer */}
        <footer className="stureg-footer">
          <span>ALL RIGHTS RESERVED BY: MKCG MEDICAL COLLEGE {new Date().getFullYear()}</span>
          <span>POWERED BY: <span className="stureg-footer-brand">Orisys Infotech</span></span>
        </footer>
      </div>
    </>
  );
}

/* ── Styles ────────────────────────────────────────────────────────────── */
const css = `
@import url('https://fonts.googleapis.com/css2?family=Outfit:wght@300;400;500;600;700;800&family=Noto+Sans+Odia&display=swap');

*, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }

:root {
  --blue-deep:   #184381;
  --blue-mid:    #184381;
  --blue-bright: #2b68c3;
  --blue-glow:   #2b68c3;
  --accent:      #2b68c3;
  --glass-bg:    #ffffff;
  --glass-border:#c7d6ec;
  --text-primary:#184381;
  --text-muted:  #4a6fa5;
  --error:       #e0526a;
  --success:     #184381;
  --green-grad:  linear-gradient(90deg, #184381 0%, #2b68c3 100%);
}

.stureg-page {
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  font-family: 'Outfit', sans-serif;
  position: relative;
  overflow-x: hidden;
}
.stureg-orb {
  position: fixed; border-radius: 50%; filter: blur(100px);
  opacity: 0.28; pointer-events: none; z-index: 0;
  animation: orb-drift 12s ease-in-out infinite alternate;
}
.stureg-orb-1 { width:500px;height:500px;background:radial-gradient(circle,#ffffff,transparent 70%);top:-120px;left:-80px; }
.stureg-orb-2 { width:400px;height:400px;background:radial-gradient(circle,#ffffff,transparent 70%);bottom:-80px;right:10%;animation-delay:-6s;animation-duration:15s; }
@keyframes orb-drift { 0%{transform:translate(0,0) scale(1)} 100%{transform:translate(30px,20px) scale(1.08)} }
.stureg-grid {
  position:fixed;inset:0;pointer-events:none;z-index:0;
  background-image:linear-gradient(rgba(255,255,255,0.06) 1px,transparent 1px),linear-gradient(90deg,rgba(255,255,255,0.06) 1px,transparent 1px);
  background-size:60px 60px;
}

/* Header */
.stureg-header {
  position:relative;z-index:10;display:flex;align-items:center;justify-content:space-between;
  padding:0.6rem 2rem;background:linear-gradient(90deg,#184381 0%,#0f3266 100%);
  border-bottom:1px solid rgba(255,255,255,0.15);flex-wrap:wrap;gap:0.5rem;
  animation:slide-down 0.6s cubic-bezier(0.22,1,0.36,1) both;
}
@keyframes slide-down { from{transform:translateY(-40px);opacity:0} to{transform:translateY(0);opacity:1} }
.stureg-header-left { display:flex;align-items:center;gap:1rem; }
.stureg-logo { height:52px;width:auto;filter:drop-shadow(0 0 8px rgba(255,255,255,0.3)); }
.stureg-hname { font-size:0.88rem;font-weight:700;color:#fff;letter-spacing:0.02em; }
.stureg-hodia { font-family:'Noto Sans Odia',sans-serif;font-size:0.72rem;color:rgba(255,255,255,0.7);margin-top:2px; }
.stureg-header-right { display:flex;align-items:center;gap:0.75rem; }
.stureg-interface-tag { font-size:0.78rem;font-weight:700;letter-spacing:0.1em;color:rgba(255,255,255,0.8); }
.stureg-home-btn {
  padding:7px 18px;background:#fff;color:#184381;font-size:0.78rem;font-weight:700;
  border-radius:6px;text-decoration:none;letter-spacing:0.04em;transition:filter 0.2s,transform 0.2s;
  box-shadow:0 2px 12px rgba(0,0,0,0.2);
}
.stureg-home-btn:hover { transform:translateY(-1px);box-shadow:0 4px 14px rgba(0,0,0,0.25); }

/* Main */
.stureg-main {
  flex:1;display:flex;justify-content:center;padding:2.5rem 1rem 3rem;
  position:relative;z-index:2;
}

/* Card */
.stureg-card {
  background:#ffffff;
  border:1px solid #e3ebf7;border-radius:24px;overflow:hidden;
  box-shadow:0 8px 40px rgba(24,67,129,0.35);
  animation:card-in 0.7s cubic-bezier(0.22,1,0.36,1) 0.1s both;
  width:100%;max-width:960px;
}
@keyframes card-in { from{transform:translateY(30px) scale(0.97);opacity:0} to{transform:translateY(0) scale(1);opacity:1} }

.stureg-card-header {
  background:linear-gradient(135deg,#184381,#2b68c3);padding:14px 24px;
  display:flex;align-items:center;justify-content:space-between;flex-wrap:wrap;gap:0.5rem;
  box-shadow:0 2px 20px rgba(24,67,129,0.4);position:relative;overflow:hidden;
}
.stureg-card-header::after {
  content:'';position:absolute;inset:0;
  background:linear-gradient(90deg,transparent,rgba(255,255,255,0.1),transparent);
  animation:shimmer-h 3s ease infinite;
}
@keyframes shimmer-h { 0%{transform:translateX(-100%)} 60%{transform:translateX(100%)} 100%{transform:translateX(100%)} }
.stureg-card-header-text { display:flex;flex-direction:column;gap:2px; }
.stureg-card-header-text span:first-child { font-size:1rem;font-weight:800;color:#fff;letter-spacing:0.1em;text-transform:uppercase; }
.stureg-card-step { font-size:0.9rem;color:rgba(255,255,255,0.75);font-weight:400; }
.stureg-status-badge { font-size:0.78rem;font-weight:600;color:rgba(255,255,255,0.8);background:rgba(0,0,0,0.2);padding:5px 14px;border-radius:20px; }
.stureg-incomplete { color:#ffd700; }

/* Info strip */
.stureg-info-strip {
  background:#eaf1fb;border-bottom:1px solid #c7d6ec;
  border-left:4px solid #2b68c3;
  padding:15px 10px;
  box-shadow:inset 0 1px 0 rgba(255,255,255,0.6);
}
.stureg-info-hint { font-size:1rem;color:#184381;font-weight:600;margin-bottom:10px; }
.stureg-info-row { display:flex;flex-wrap:wrap;gap:0.5rem; }
.stureg-info-item {
  display:flex;gap:6px;align-items:center;
  background:#ffffff;border:1px solid #c7d6ec;border-radius:20px;
  padding:5px 14px;
}
.stureg-info-key { font-size:0.75rem;font-weight:600;color:#2b68c3;letter-spacing:0.05em;text-transform:uppercase; }
.stureg-info-val {
  font-size: 0.82rem;
  font-weight: 700;
  background: linear-gradient(135deg, #0d6efd, #20529b);
  -webkit-background-clip: text;
  background-clip: text;
  -webkit-text-fill-color: transparent;
  color: transparent; /* fallback for browsers that don't support background-clip: text */
}
.stureg-submit-error {
  margin:12px 24px 0;padding:10px 16px;background:#fdecef;
  border:1px solid #f3c6cc;border-radius:8px;color:var(--error);font-size:0.82rem;
}

/* Form */
.stureg-form { padding:1.5rem 1.5rem 2rem; }

/* Grid layouts */
.stureg-grid-3 { display:grid;grid-template-columns:repeat(3,1fr);gap:1rem 1.25rem;margin-bottom:1rem; }
.stureg-grid-2 { display:grid;grid-template-columns:repeat(2,1fr);gap:1rem 1.25rem;margin-bottom:1rem; }
@media(max-width:768px) { .stureg-grid-3,.stureg-grid-2 { grid-template-columns:1fr 1fr; } }
@media(max-width:520px) { .stureg-grid-3,.stureg-grid-2 { grid-template-columns:1fr; } }

/* Section heading */
.stureg-section-heading {
  display:flex;align-items:center;gap:10px;
  font-size:0.88rem;font-weight:700;color:#184381;letter-spacing:0.06em;text-transform:uppercase;
  margin:1.5rem 0 0.75rem;padding-bottom:8px;border-bottom:1px solid #dce6f5;
}
.stureg-section-dot { width:8px;height:8px;border-radius:50%;background:#2b68c3;flex-shrink:0; }
.stureg-same-check {
  display:inline-flex;align-items:center;gap:6px;margin-left:12px;
  font-size:0.78rem;color:#4a6fa5;font-weight:500;cursor:pointer;text-transform:none;letter-spacing:0;
}
.stureg-same-check input { accent-color:#184381;cursor:pointer; }

/* Field */
.stureg-field { display:flex;flex-direction:column;gap:5px; }
.stureg-label { font-size:0.73rem;font-weight:600;color:#3e6bb0;letter-spacing:0.05em;text-transform:uppercase; }
.stureg-req { color:var(--error); }
.stureg-error-msg { color:var(--error);font-size:0.72rem;display:flex;align-items:center;gap:4px;animation:shake 0.3s ease; }
@keyframes shake { 0%,100%{transform:translateX(0)} 25%{transform:translateX(-3px)} 75%{transform:translateX(3px)} }

/* Input wrap */
.stureg-input-wrap {
  display:flex;align-items:center;background:#f5f8fc;
  border:1px solid #dce6f5;border-radius:9px;overflow:hidden;
  transition:border-color 0.25s,box-shadow 0.25s,background 0.25s;
}
.stureg-input-wrap:focus-within {
  border-color:var(--blue-glow);background:#fff;
  box-shadow:0 0 0 3px rgba(43,104,195,0.18);
}
.stureg-input-wrap.stureg-err { border-color:var(--error);box-shadow:0 0 0 3px rgba(224,82,106,0.14); }
.stureg-input-wrap input,
.stureg-input-wrap textarea {
  flex:1;border:none;outline:none;background:transparent;color:#333;
  font-size:0.88rem;font-family:'Outfit',sans-serif;padding:10px 13px;width:100%;
}
.stureg-input-wrap textarea { resize:vertical;min-height:58px; }
.stureg-input-wrap input::placeholder,
.stureg-input-wrap textarea::placeholder { color:#9fb3d1; }
.stureg-input-wrap input:disabled,
.stureg-input-wrap select:disabled,
.stureg-input-wrap textarea:disabled { opacity:0.55;cursor:not-allowed; }

/* Select */
.stureg-select-wrap { position:relative; }
.stureg-select-wrap select {
  flex:1;border:none;outline:none;background:transparent;color:#333;
  font-size:0.88rem;font-family:'Outfit',sans-serif;padding:10px 32px 10px 13px;
  appearance:none;-webkit-appearance:none;cursor:pointer;width:100%;
}
.stureg-select-wrap select option { background:#fff;color:#184381; }
.stureg-select-arrow { position:absolute;right:11px;top:50%;transform:translateY(-50%);color:#6e93c9;pointer-events:none; }
.stureg-textarea-wrap { align-items:flex-start; }

/* File input */
.stureg-file-wrap {
  display:flex;align-items:center;flex-wrap:wrap;gap:8px;
  background:#f5f8fc;border:1px solid #dce6f5;
  border-radius:9px;padding:8px 12px;
}
.stureg-file-wrap.stureg-err { border-color:var(--error);box-shadow:0 0 0 3px rgba(224,82,106,0.14); }
.stureg-file-label {
  display:inline-flex;align-items:center;gap:5px;cursor:pointer;
  background:#e7eef9;border:1px solid #c7d6ec;
  padding:5px 12px;border-radius:5px;font-size:0.78rem;color:#184381;font-weight:600;
  transition:background 0.2s;white-space:nowrap;
}
.stureg-file-label:hover { background:#dbe6f7; }
.stureg-file-name { font-size:0.75rem;color:#7189b0;flex:1; }
.stureg-file-hint { width:100%;font-size:0.68rem;color:#c07a00;margin-top:1px; }

/* Exam table */
.stureg-exam-table { margin-bottom:1rem; }
.stureg-exam-header {
  display:grid;grid-template-columns:1.5fr 1fr 1.5fr 1.5fr 1fr 36px;
  gap:8px;padding:0 0 6px;border-bottom:1px solid #e3ebf7;
  font-size:0.7rem;font-weight:700;color:#6e93c9;letter-spacing:0.06em;text-transform:uppercase;
}
.stureg-exam-row {
  display:grid;grid-template-columns:1.5fr 1fr 1.5fr 1.5fr 1fr 36px;
  gap:8px;padding:7px 0;border-bottom:1px solid #eef3fa;
}
.stureg-exam-input input, .stureg-exam-input select { padding:8px 10px;font-size:0.82rem; }
.stureg-exam-input.stureg-select-wrap { position:relative; }
.stureg-exam-btn {
  width:34px;height:34px;border-radius:8px;border:none;cursor:pointer;
  display:flex;align-items:center;justify-content:center;font-size:1rem;font-weight:700;
  background:rgba(24,67,129,0.12);color:#184381;transition:background 0.2s,transform 0.15s;
  align-self:center;
}
.stureg-exam-btn:hover { background:rgba(24,67,129,0.22);transform:scale(1.08); }
.stureg-exam-remove { background:rgba(224,82,106,0.12);color:#e0526a;font-size:0.8rem; }
.stureg-exam-remove:hover { background:rgba(224,82,106,0.22); }
.stureg-add-row-btn {
  margin-top:8px;display:inline-flex;align-items:center;gap:6px;
  background:none;border:1px dashed rgba(43,104,195,0.4);color:#2b68c3;
  font-size:0.78rem;font-family:'Outfit',sans-serif;font-weight:600;
  padding:7px 16px;border-radius:7px;cursor:pointer;transition:border-color 0.2s,background 0.2s;
}
.stureg-add-row-btn:hover { border-color:#2b68c3;background:rgba(43,104,195,0.08); }
@media(max-width:700px) { .stureg-exam-header{display:none} .stureg-exam-row{grid-template-columns:1fr 1fr;} }

/* Declaration */
.stureg-declaration {
  margin:1.2rem 0 1rem;padding:14px 16px;
  background:#f5f8fc;border:1px solid #dce6f5;border-radius:10px;
}
.stureg-decl-label {
  display:flex;align-items:flex-start;gap:10px;cursor:pointer;
  font-size:0.82rem;color:#4a6fa5;line-height:1.5;
}
.stureg-decl-label input { accent-color:#184381;margin-top:3px;flex-shrink:0;width:15px;height:15px;cursor:pointer; }

/* Captcha section */
.stureg-captcha-row { display:flex;align-items:center;gap:10px;margin-top:8px;flex-wrap:wrap; }
.stureg-captcha-input { flex:1;max-width:270px; }
.stureg-refresh-btn {
  width:34px;height:34px;flex-shrink:0;
  display:flex;align-items:center;justify-content:center;
  border:0;border-radius:50%;
  color:#fff;background:linear-gradient(90deg,#184381 0%,#2b68c3 100%);
  cursor:pointer;box-shadow:0 3px 8px rgba(24,67,129,0.35);
  transition:transform 0.2s ease;
}
.stureg-refresh-btn:hover { transform:rotate(90deg); }
@media(max-width:480px) {
  .stureg-captcha-row { flex-wrap:wrap; }
  .stureg-captcha-input { flex:1 1 100%; margin-top:8px; }
}

/* Submit */
.stureg-submit-row { display:flex;justify-content:flex-end;margin-top:1.5rem;padding-top:1.25rem;border-top:1px solid #e3ebf7; }
.stureg-submit-btn {
  padding:13px 36px;background:var(--green-grad);color:#fff;
  font-size:0.92rem;font-weight:700;font-family:'Outfit',sans-serif;letter-spacing:0.05em;
  border:none;border-radius:10px;cursor:pointer;display:flex;align-items:center;gap:8px;
  box-shadow:0 4px 20px rgba(24,67,129,0.45);transition:transform 0.2s,box-shadow 0.2s,filter 0.2s;
  position:relative;overflow:hidden;
}
.stureg-submit-btn:hover:not(:disabled) { transform:translateY(-2px);box-shadow:0 8px 28px rgba(24,67,129,0.55);filter:brightness(1.08); }
.stureg-submit-btn:disabled { opacity:0.65;cursor:not-allowed; }
.stureg-btn-spinner {
  display:inline-block;width:16px;height:16px;
  border:2px solid rgba(255,255,255,0.4);border-top-color:#fff;
  border-radius:50%;animation:spin 0.7s linear infinite;
}
@keyframes spin { to{transform:rotate(360deg)} }

/* Success */
.stureg-success-card {
  display:flex;flex-direction:column;align-items:center;text-align:center;
  padding:3.5rem 2rem;gap:1.2rem;
}
.stureg-success-ring { position:relative;width:90px;height:90px; }
.stureg-success-ring::before {
  content:'';position:absolute;inset:-4px;border-radius:50%;
  background:conic-gradient(#184381,#2b68c3,#184381);animation:spin-ring 2s linear infinite;
}
@keyframes spin-ring { to{transform:rotate(360deg)} }
.stureg-success-icon {
  position:relative;width:90px;height:90px;border-radius:50%;
  background:rgba(24,67,129,0.12);border:1px solid rgba(43,104,195,0.35);
  color:var(--success);font-size:2.2rem;display:flex;align-items:center;justify-content:center;
  z-index:1;animation:pop-in 0.5s cubic-bezier(0.34,1.56,0.64,1) both;
}
@keyframes pop-in { from{transform:scale(0);opacity:0} to{transform:scale(1);opacity:1} }
.stureg-success-title { font-size:1.6rem;font-weight:800;color:#184381; }
.stureg-success-sub { color:var(--text-muted);font-size:0.88rem;max-width:380px;line-height:1.6; }
.stureg-spinner-bar { width:180px;height:3px;border-radius:2px;background:#e7ecfb;overflow:hidden; }
.stureg-spinner-bar::after {
  content:'';display:block;height:100%;width:40%;border-radius:2px;
  background:var(--green-grad);animation:bar-sweep 1.5s ease infinite;
}
@keyframes bar-sweep { 0%{transform:translateX(-100%)} 100%{transform:translateX(350%)} }
.stureg-dashboard-btn {
  padding:12px 28px;background:var(--green-grad);color:#fff;font-weight:700;font-size:0.88rem;
  border-radius:10px;text-decoration:none;letter-spacing:0.04em;
  box-shadow:0 4px 18px rgba(24,67,129,0.4);transition:filter 0.2s,transform 0.2s;
}
.stureg-dashboard-btn:hover { filter:brightness(1.1);transform:translateY(-1px); }

/* Footer */
.stureg-footer {
  position:relative;z-index:10;display:flex;justify-content:space-between;align-items:center;
  padding:0.6rem 2rem;background:linear-gradient(90deg,#0f3266,#184381 100%);
  border-top:1px solid rgba(255,255,255,0.15);font-size:0.72rem;
  color:rgba(255,255,255,0.65);letter-spacing:0.04em;flex-wrap:wrap;gap:0.25rem;
}
.stureg-footer-brand { color:#a9c9f5;font-weight:700; }

@media(max-width:600px) {
  .stureg-header { padding:0.5rem 1rem; }
  .stureg-hname  { font-size:0.75rem; }
  .stureg-form   { padding:1rem 1rem 1.5rem; }
  .stureg-interface-tag { display:none; }
  .stureg-info-row { gap:0.75rem; }
}

.preview-overlay{
  position:fixed;
  inset:0;
  background:rgba(15,35,70,.55);
  backdrop-filter:blur(8px);
  z-index:99999;
  display:flex;
  justify-content:center;
  align-items:center;
  padding:20px;
}
.preview-modal {
  width:100%;max-width:1200px;max-height:95vh;overflow:auto;
  border-radius:24px;background:#ffffff;
  border:1px solid #e3ebf7;
  box-shadow:0 20px 60px rgba(24,67,129,0.35);
}

.preview-header{
  position:relative;
  display:flex;
  flex-direction:column;
  align-items:center;
  text-align:center;
  gap:6px;
  padding:26px 60px 22px;
  border-bottom:1px solid #e3ebf7;
  background:linear-gradient(180deg,#f5f8fc 0%,#ffffff 100%);
}

.preview-header h2{
  color:#184381;
  font-size:1.5rem;
  font-weight:800;
  letter-spacing:0.01em;
  margin:0;
}

.preview-header-sub{
  color:#4a6fa5;
  font-size:0.85rem;
  font-weight:500;
  margin:0;
}

.preview-close{
  position:absolute;
  top:16px;
  right:20px;
  width:34px;
  height:34px;
  display:flex;
  align-items:center;
  justify-content:center;
  background:#f5f8fc;
  border:1px solid #dce6f5;
  border-radius:50%;
  color:#4a6fa5;
  font-size:18px;
  cursor:pointer;
  transition:background 0.2s,color 0.2s,transform 0.2s;
}

.preview-close:hover{
  background:#fdecef;
  color:#e0526a;
  transform:rotate(90deg);
}

.preview-images{
  display:flex;
  gap:20px;
  padding:25px;
  flex-wrap:wrap;
}

.preview-image-card{
  width:180px;
  text-align:center;
}

.preview-image-card h4{
  color:#2b68c3;
  margin-bottom:10px;
}

.preview-image-card img{
  width:100%;
  height:180px;
  object-fit:cover;
  border-radius:15px;
  border:2px solid #dce6f5;
}

.preview-section{
  padding:20px 30px;
}

.preview-section h3{
  color:#184381;
  margin-bottom:20px;
  border-bottom:1px solid #dce6f5;
  padding-bottom:10px;
}

.preview-grid{
  display:grid;
  grid-template-columns:repeat(auto-fit,minmax(250px,1fr));
  gap:15px;
}

.preview-grid div{
  background:#f5f8fc;
  padding:15px;
  border-radius:12px;
}

.preview-grid label{
  display:block;
  color:#2b68c3;
  font-size:12px;
  text-transform:uppercase;
  margin-bottom:6px;
}

.preview-grid span{
  color:#184381;
  font-size:15px;
}

.preview-table{
  width:100%;
  border-collapse:collapse;
}

.preview-table th{
  background:#2b68c3;
  color:#fff;
  padding:12px;
}

.preview-table td{
  padding:12px;
  color:#333;
  border-bottom:1px solid #eef3fa;
}

.preview-footer{
  display:flex;
  justify-content:flex-end;
  gap:15px;
  padding:25px 30px;
  border-top:1px solid #e3ebf7;
}

.preview-edit-btn{
  background:#6c757d;
  color:#fff;
  border:none;
  padding:12px 25px;
  border-radius:10px;
  cursor:pointer;
}

.preview-submit-btn{
  background:linear-gradient(135deg,#184381,#2b68c3);
  color:#fff;
  border:none;
  padding:12px 25px;
  border-radius:10px;
  font-weight:700;
  cursor:pointer;
}

.stureg-success-actions{
  display:flex;
  gap:15px;
  margin-top:20px;
  flex-wrap:wrap;
  justify-content:center;
}

.stureg-print-btn{
  padding:12px 28px;
  border:none;
  border-radius:10px;
  cursor:pointer;
  font-weight:700;
  color:#fff;
  background:linear-gradient(135deg,#184381,#2b68c3);
  box-shadow:0 4px 18px rgba(24,67,129,.4);
}

.stureg-print-btn:hover{
  transform:translateY(-2px);
}

/* ── Success Card ─────────────────────────────────────────────────── */
.stureg-success-card {
  display:flex;flex-direction:column;align-items:center;text-align:center;
  padding:2.5rem 2rem 2rem;gap:0;width:100%;max-width:960px;
}

/* Animated SVG check */
.sc-check-wrap {
  margin-bottom:1.5rem;
}
.sc-check-svg {
  width:72px;height:72px;
}
.sc-check-circle {
  stroke:#184381;stroke-width:2;
  stroke-dasharray:157;stroke-dashoffset:157;
  animation:draw-circle 0.6s cubic-bezier(0.65,0,0.45,1) 0.2s forwards;
}
.sc-check-tick {
  stroke:#184381;stroke-width:3;stroke-linecap:round;stroke-linejoin:round;
  stroke-dasharray:48;stroke-dashoffset:48;
  animation:draw-tick 0.4s cubic-bezier(0.65,0,0.45,1) 0.8s forwards;
}
@keyframes draw-circle { to{stroke-dashoffset:0} }
@keyframes draw-tick   { to{stroke-dashoffset:0} }

/* Title block */
.sc-title-block { margin-bottom:1.2rem; }
.sc-title {
  font-size:1.55rem;font-weight:800;color:#184381;
  letter-spacing:0.01em;margin-bottom:0.4rem;
}
.sc-ref {
  font-size:0.78rem;color:#6e93c9;
  letter-spacing:0.05em;text-transform:uppercase;
}
.sc-ref span {
  color:#184381;font-weight:700;letter-spacing:0.1em;
  background:#eaf1fb;padding:2px 10px;border-radius:5px;
  border:1px solid #c7d6ec;margin-left:4px;
}

/* Divider */
.sc-divider {
  width:100%;height:1px;
  background:linear-gradient(90deg,transparent,#dce6f5,transparent);
  margin-bottom:1.4rem;
}

/* Message box */
.sc-message-box {
  display:flex;align-items:flex-start;gap:14px;text-align:left;
  background:#f5f8fc;border:1px solid #e3ebf7;
  border-left:3px solid #2b68c3;border-radius:10px;
  padding:16px 20px;margin-bottom:1.5rem;width:100%;max-width:640px;
}
.sc-message-icon { font-size:1.3rem;flex-shrink:0;margin-top:2px; }
.sc-message-text { display:flex;flex-direction:column;gap:6px; }
.sc-message-text p { font-size:0.84rem;color:#4a6fa5;line-height:1.65; }
.sc-message-text strong { color:#184381;font-weight:700; }

/* Steps */
.sc-steps {
  display:flex;align-items:flex-start;justify-content:center;
  gap:0;margin-bottom:1.5rem;width:100%;max-width:680px;
  flex-wrap:wrap;
}
.sc-step {
  display:flex;flex-direction:column;align-items:center;gap:5px;
  position:relative;flex:1;min-width:120px;padding:12px 8px;
  border-radius:10px;
  background:#f5f8fc;border:1px solid #e3ebf7;
}
.sc-step-done {
  background:#eaf1fb;border-color:#c7d6ec;
}
.sc-step-icon { font-size:1.3rem; }
.sc-step-label {
  font-size:0.72rem;font-weight:700;color:#4a6fa5;
  letter-spacing:0.03em;text-align:center;
}
.sc-step-sub {
  font-size:0.64rem;color:#8ba3c9;
  text-transform:uppercase;letter-spacing:0.05em;
}
.sc-step-done .sc-step-label { color:#184381; }
.sc-step-done .sc-step-sub   { color:rgba(43,104,195,0.7); }
.sc-step-arrow {
  position:absolute;right:-14px;top:50%;transform:translateY(-50%);
  color:#b9cae7;font-size:1rem;z-index:1;
}

/* Info chips */
.sc-chips {
  display:flex;flex-wrap:wrap;justify-content:center;gap:10px;
  margin-bottom:1.8rem;
}
.sc-chip {
  display:inline-flex;align-items:center;gap:7px;
  font-size:0.75rem;font-weight:600;color:#4a6fa5;
  background:#f5f8fc;border:1px solid #e3ebf7;
  padding:6px 14px;border-radius:20px;letter-spacing:0.03em;
}
.sc-chip-dot {
  width:7px;height:7px;border-radius:50%;flex-shrink:0;
}
.sc-chip-green { background:#184381;box-shadow:0 0 6px rgba(24,67,129,0.6); }
.sc-chip-blue  { background:#2b68c3;box-shadow:0 0 6px rgba(43,104,195,0.6); }
.sc-chip-gold  { background:#f59e0b;box-shadow:0 0 6px rgba(245,158,11,0.6); }

/* Action buttons */
.sc-actions {
  display:flex;gap:14px;flex-wrap:wrap;justify-content:center;
  margin-bottom:1.4rem;
}
.sc-btn-print {
  display:inline-flex;align-items:center;gap:8px;
  padding:13px 28px;
  background:#f5f8fc;
  border:1px solid #c7d6ec;
  color:#184381;font-size:0.88rem;font-weight:700;
  font-family:'Outfit',sans-serif;letter-spacing:0.04em;
  border-radius:10px;cursor:pointer;
  transition:background 0.2s,transform 0.2s,border-color 0.2s;
}
.sc-btn-print:hover {
  background:#e7eef9;
  border-color:#a9c1e0;
  transform:translateY(-2px);
}
.sc-btn-dashboard {
  display:inline-flex;align-items:center;gap:8px;
  padding:13px 28px;
  background:linear-gradient(90deg,#184381 0%,#2b68c3 100%);
  border:none;color:#fff;font-size:0.88rem;font-weight:700;
  font-family:'Outfit',sans-serif;letter-spacing:0.04em;
  border-radius:10px;text-decoration:none;
  box-shadow:0 4px 18px rgba(24,67,129,0.4);
  transition:filter 0.2s,transform 0.2s;
}
.sc-btn-dashboard:hover { filter:brightness(1.1);transform:translateY(-2px); }

/* Footnote */
.sc-footnote {
  font-size:0.72rem;color:#8ba3c9;
  letter-spacing:0.04em;text-align:center;max-width:480px;line-height:1.6;
}

`;
